// ============================================================================
// SHARED TYPES - Used in both Frontend and Backend
// ============================================================================

import type { Timestamp } from './timestamp';
export type { Timestamp } from './timestamp';
export type {
  EmergencyCardDocument,
  EmergencyCardData,
  EmergencyAccessLog,
  ListEmergencyAccessLogsRequest,
  ListEmergencyAccessLogsResponse,
} from './emergency';
export {
  MODULE_REGISTRY,
  MODULE_KEYS,
  isValidModuleKey,
  SCHOOL_YEAR_PATTERN,
  isValidSchoolYear,
} from './settings';
export type {
  ModuleKey,
  ModuleRegistryEntry,
  SchoolSettings,
  SchoolYearDates,
  SaveSchoolSettingsRequest,
  GetActiveSchoolSettingsResponse,
} from './settings';
// Module 8 — Capacity Decisions (Rooms, Curriculum, Algorithm)
export {
  DAYS_OF_WEEK,
  REARRANGE_MODES,
  REARRANGE_STABILITY_LEVELS,
  isRearrangeMode,
  isRearrangeStabilityLevel,
} from './module8';
export type {
  DayOfWeek,
  TimeBlock,
  Venue,
  VenuesConfig,
  Room,
  SchoolConfigBundle,
  ExportSchoolConfigResponse,
  ImportSchoolConfigRequest,
  ImportSchoolConfigResponse,
  CopyCategoriesToYearRequest,
  CopyCategoriesToYearResponse,
  ImportScheduleRow,
  ImportScheduleRequest,
  ImportScheduleResponse,
  SaveVenuesRequestVenue,
  SaveVenuesRequest,
  SaveVenuesResponse,
  CreateRoomRequest,
  CreateRoomResponse,
  UpdateRoomRequest,
  UpdateRoomResponse,
  DeleteRoomRequest,
  DeleteRoomResponse,
  ListRoomsRequest,
  ListRoomsResponse,
  CapacityRowStatus,
  CapacityDashboardRow,
  CapacityAggregate,
  GetCapacityDashboardRequest,
  GetCapacityDashboardResponse,
  HolidayType,
  Holiday,
  CreateHolidayRequest,
  CreateHolidayResponse,
  UpdateHolidayRequest,
  UpdateHolidayResponse,
  DeleteHolidayRequest,
  DeleteHolidayResponse,
  ListHolidaysRequest,
  ListHolidaysResponse,
  ImportHolidayRow,
  BulkImportHolidaysRequest,
  BulkImportHolidaysResponse,
  ImportRowError,
  BlockedByClassRef,
  BlockedBySlotRef,
  ValidationSeverity,
  ValidationIssueCode,
  ValidationEntityType,
  ValidationIssue,
  ValidateScheduleDataRequest,
  ValidateScheduleDataResponse,
  HardConstraintCode,
  SoftConstraintCode,
  ConstraintRelaxations,
  PlacementReason,
  RejectedAlternative,
  PlacementSuggestion,
  FilterStats,
  PlacementDiagnostic,
  SuggestPlacementRequest,
  SuggestPlacementResponse,
  ConfirmPlacementRequest,
  ConfirmPlacementResponse,
  CategoryGroupingTag,
  CategoryDefinition,
  CreateCategoryRequest,
  CreateCategoryResponse,
  UpdateCategoryRequest,
  UpdateCategoryResponse,
  DeleteCategoryRequest,
  DeleteCategoryResponse,
  ListCategoriesRequest,
  ListCategoriesResponse,
  ImportCategoryRow,
  BulkImportCategoriesRequest,
  BulkImportCategoriesResponse,
  ScheduleVersionType,
  ScheduleVersionClassEntry,
  ScheduleVersionSipEntry,
  ScheduleVersion,
  AlgorithmRunMode,
  AlgorithmRunStatus,
  AlgorithmRejectedAlternative,
  AlgorithmAlternative,
  AlgorithmConflict,
  AlgorithmRun,
  CustomConstraintTemplate,
  CustomConstraint,
  TeacherUnavailabilityReason,
  TeacherUnavailability,
  AlgorithmSettings,
  // Phase 5B — RearrangeJob
  RearrangeJobStatus,
  RearrangeMode,
  RearrangeStabilityLevel,
  RearrangeLocks,
  ProposedChangeSlot,
  ProposedChange,
  RearrangeCostBreakdown,
  RearrangeJob,
  StartRearrangeJobRequest,
  StartRearrangeJobResponse,
  GetRearrangeJobRequest,
  GetRearrangeJobResponse,
  ListRearrangeJobsRequest,
  ListRearrangeJobsResponse,
  CommitRearrangeJobRequest,
  CommitRearrangeJobResponse,
  DiscardRearrangeJobRequest,
  DiscardRearrangeJobResponse,
  CancelRearrangeJobRequest,
  CancelRearrangeJobResponse,
  // Migration — dayOfWeek кирилица → английски
  MigrateScheduleSlotDaysRequest,
  MigrateScheduleSlotDaysResponse,
  MigrateScheduleSlotDaySample,
  // Cleanup — orphan childId references
  CleanupOrphanChildrenRequest,
  CleanupOrphanChildrenResponse,
  OrphanChildSample,
  // P4 slots-single-truth — планова опашка
  PlannedAssignment,
  PlannedAssignmentStatus,
  CreatePlannedAssignmentRequest,
  CreatePlannedAssignmentResponse,
  AcknowledgeUnqualified,
  UnqualifiedOverrideRecord,
  CancelPlannedAssignmentRequest,
  CancelPlannedAssignmentResponse,
} from './module8';

import type {
  AcknowledgeUnqualified,
  CategoryOverflow,
  TeacherCalendarBounds,
  UnqualifiedOverrideRecord,
} from './module8';
export type { CategoryOverflow, TeacherCalendarBounds };

// Module 9 — Teacher Hours & Salary (Payroll)
export { schoolYearKey, PAY_ITEM_KINDS, AUSTRIAN_PAY_CLASSES } from './module9';
export type {
  MonthKey,
  PayItemKind,
  AustrianPayClass,
  PayItemCadence,
  PayItemType,
  CategoryRate,
  PayrollSettlementConfig,
  PayrollSettings,
  SvComponent,
  SvTable,
  TaxBracketMonthly,
  PayrollTaxConfig,
  HoursSubmissionStatus,
  HoursSubmissionSource,
  HoursEntry,
  HoursValidationIssue,
  HoursValidationResult,
  HoursSubmission,
  EarningLine,
  PayrollConfigRef,
  ComputedDocSource,
  WorkLog,
  SettlementStatus,
  SettlementDistribution,
  CarryoverDecision,
  SalarySettlement,
  SalaryPaymentSource,
  SalaryPaymentStatus,
  PaymentAllocation,
  SalaryPayment,
  BankImport,
  DirectorPayment,
  PayrollBankRowInput,
  PayrollRowView,
  PreviewPayrollBankImportRequest,
  PreviewPayrollBankImportResponse,
  PayrollBankRowChoice,
  ConfirmPayrollBankImportRequest,
  PayrollImportOutcome,
  PayrollImportRowResult,
  ConfirmPayrollBankImportResponse,
  PayrollContractType,
  TeacherPayrollYear,
} from './module9';

export type Language = 'bg' | 'de' | 'en';

export type UserRole = 'superadmin' | 'admin' | 'parent' | 'teacher';

export type AssignmentStatus = 'pending' | 'signed' | 'declined' | 'revoked';

export type ResponseType = 'signed' | 'declined';

export type DeliveryStatus = 'sent' | 'delivered' | 'bounced' | 'failed';

export type CommunicationChannel = 'email' | 'sms' | 'push';

export type SignatureType = 'canvas' | 'typed' | 'uploaded' | 'code';

export type DeclarationPeriodicity = 'YEARLY' | 'ONCE' | 'SEMESTER';

/**
 * GDPR legal basis for data processing (Art. 6 + Art. 9).
 * Each declaration must declare which article justifies the processing.
 * Stored both on the Declaration document (source of truth) and on each
 * Signature (snapshot at time of consent, for audit independence).
 */
export type GdprLegalBasis =
  | 'Art6(1)(a)'   // Consent
  | 'Art6(1)(b)'   // Contractual necessity
  | 'Art6(1)(c)'   // Legal obligation
  | 'Art6(1)(d)'   // Vital interests
  | 'Art6(1)(e)'   // Public interest / official authority
  | 'Art6(1)(f)'   // Legitimate interests
  | 'Art9(2)(a)'   // Explicit consent for special categories (health)
  | 'Art9(2)(h)';  // Preventive/occupational medicine

/**
 * Default legal basis per declaration code. Used when a Declaration document
 * doesn't have an explicit `legalBasis` field (backward compat / migration).
 */
export const DEFAULT_LEGAL_BASIS: Record<string, GdprLegalBasis> = {
  GDPR: 'Art6(1)(a)',
  MEDICAL_INFO: 'Art9(2)(a)',
  EMERGENCY_CONTACTS: 'Art6(1)(d)',
  PHOTO_CONSENT: 'Art6(1)(a)',
  SCHOOL_RULES: 'Art6(1)(b)',
  PICKUP_AUTH: 'Art6(1)(f)',
};

export type EmailTemplateType =
  | 'INVITATION'
  | 'WELCOME'
  | 'REMINDER'
  | 'SIGNATURE_CONFIRMATION'
  | 'PASSWORD_RESET'
  | 'REGISTRATION_REMINDER'
  | 'FEE_REMINDER_BEFORE'
  | 'FEE_REMINDER_OVERDUE'
  | 'FEE_PAYMENT_RECEIVED'
  | 'FEE_PAYMENT_CONFIRMED'
  | 'FEE_PAYMENT_REJECTED'
  | 'ENROLLMENT_RECEIVED'
  | 'ENROLLMENT_APPROVED'
  | 'ENROLLMENT_REJECTED'
  | 'VOLUNTEER_APPLICATION_RECEIVED'
  | 'VOLUNTEER_INVITATION'
  | 'TEACHER_INVITATION';

export type EnrollmentStatus = 'pending' | 'approved' | 'rejected';

// ----------------------------------------------------------------------------
// Admin migration — re-stamp enrollments from one school year to another.
// Use case: enrollments mis-stamped with a stale year (legacy schoolData.schoolYear
// drift) need to move to the correct active enrollment year. dryRun by default.
// ----------------------------------------------------------------------------
export interface RestampEnrollmentsYearRequest {
  toYear: string;
  fromYear?: string;           // omit → all pending NOT already on toYear
  dryRun?: boolean;            // default true (safe preview)
  status?: EnrollmentStatus;   // default 'pending'
}

export interface RestampEnrollmentSample {
  enrollmentId: string;
  tempMemberNo: string;
  childName: string | null;
  currentYear: string;
}

export interface RestampEnrollmentsYearResponse {
  dryRun: boolean;
  fromYear: string | null;     // null when bulk (all non-toYear)
  toYear: string;
  status: EnrollmentStatus;
  matched: number;
  updated: number;
  samples: RestampEnrollmentSample[];
}

// ============================================================================
// SCHOOL
// ============================================================================

export interface School {
  schoolId: string;
  name: {
    bg: string;
    de: string;
    en: string;
  };
  location: {
    city: string;
    country: string;
    timezone: string;
    address: string;
  };
  schoolYear: string;
  languages: Language[];
  defaultLanguage: Language;
  gdpr: {
    dataControllerName: string;
    /**
     * @deprecated Use `dpo` instead. Kept for backwards compatibility —
     * reads still work, but new writes should populate `dpo`.
     */
    dataProtectionOfficer: string;
    /**
     * Structured DPO contact (GDPR Art. 37-39). Required for schools
     * processing health data (Art. 9), which this system does via
     * emergency cards. Displayed on Privacy Policy page.
     */
    dpo?: DpoContact;
    privacyPolicyUrl: string;
    dataRetentionYears: number;
  };
  /** Branding settings for visual identity */
  branding?: SchoolBranding;
  /** Contact information */
  contact?: SchoolContact;
  /** Legal identity + bank details for formal documents (declarations, forms). */
  legal?: SchoolLegal;
  config: SchoolConfig;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Правни + банкови данни за официалните документи (декларация за часове и др.).
 * ЕДИНСТВЕН източник на истина — PDF генераторите ги дърпат през
 * getSchoolBranding. Редактируеми в Настройки на училището → Основни данни.
 * Всички полета optional (fallback към кодови defaults, ако липсват).
 */
export interface SchoolLegal {
  /** Пълно правно име (както в официалния формуляр). */
  fullLegalName?: string;
  /** Правен адрес (седалище). */
  legalAddress?: string;
  directorName?: string;
  directorTitle?: string;
  /** Регистрационен номер (ZVR — австрийско изискване за обявяване на официални документи). */
  registrationNumber?: string;
  bankName?: string;
  bankAccountNumber?: string; // Konto Nr.
  bankBlz?: string; // BLZ
  iban?: string;
  bic?: string;
  updatedAt?: Timestamp;
  updatedBy?: string;
}

/**
 * PUBLIC projection of a School — world-readable for PRE-AUTH surfaces (login,
 * enrollment, privacy pages). Lives at the top-level `schoolPublic/{schoolId}`
 * collection and contains ONLY non-PII fields: branding, localized name, and the
 * published DPO contact (a GDPR Art. 37-39 officer contact — NOT a data
 * subject's personal data). Kept in sync from `schools/{schoolId}` by the
 * syncSchoolPublic Cloud Function. The full `schools/{schoolId}` doc is now
 * auth-gated (belongsToSchool) so its config.emailTemplates / contact / location
 * no longer leak pre-auth. [C2 — security rules audit 2026-06-07]
 */
export interface SchoolPublic {
  schoolId: string;
  name: { bg: string; de: string; en: string };
  branding?: SchoolBranding;
  /** Published DPO contact subset (name/email/phone/registryNumber). */
  dpo?: Pick<DpoContact, 'name' | 'email' | 'phone' | 'registryNumber'>;
  /** @deprecated legacy free-text DPO name (from gdpr.dataProtectionOfficer). */
  dataProtectionOfficerLegacy?: string;
  updatedAt: Timestamp;
}

/**
 * Data Protection Officer contact (GDPR Art. 37-39).
 * Required for organizations processing special-category data (Art. 9)
 * or monitoring data subjects on a large scale.
 */
export interface DpoContact {
  /** Full name of the DPO */
  name: string;
  /** Email address for DPA and subject inquiries */
  email: string;
  /** Optional phone number */
  phone?: string;
  /** DPO registry number (if registered with supervisory authority — e.g. Austrian DSB) */
  registryNumber?: string;
  /** Date of appointment (ISO string or Timestamp) */
  appointedAt?: Timestamp;
  /** Last updated */
  updatedAt?: Timestamp;
  /** User who last updated (superadmin userId) */
  updatedBy?: string;
}

/** School branding/visual identity settings */
export interface SchoolBranding {
  /** Logo URL (Cloud Storage) */
  logoUrl?: string;
  /** Logo for dark backgrounds (Cloud Storage) */
  logoLightUrl?: string;
  /** Primary brand color (hex) */
  primaryColor?: string;
  /** Secondary brand color (hex) */
  secondaryColor?: string;
  /** Accent color for highlights (hex) */
  accentColor?: string;
  /** Email header background image URL */
  emailHeaderImageUrl?: string;
  /** PDF header background image URL */
  pdfHeaderImageUrl?: string;
  /** PDF footer image URL */
  pdfFooterImageUrl?: string;
  /** Favicon URL */
  faviconUrl?: string;
  /** Last updated */
  updatedAt?: Timestamp;
  updatedBy?: string;
}

/** School contact information */
export interface SchoolContact {
  email: string;
  phone?: string;
  website?: string;
  fax?: string;
}

export interface SchoolConfig {
  emailTemplates: Record<string, EmailTemplate>;
  declarationTypes: Record<string, DeclarationType>;
  features: {
    enableReminders: boolean;
    reminderIntervalDays: number;
    maxReminders: number;
    autoAssignDeclarations?: boolean;
  };
}

export interface EmailTemplate {
  subject: Record<Language, string>;
  body: Record<Language, string>;
}

export interface DeclarationType {
  title: Record<Language, string>;
  required: boolean;
  active: boolean;
  periodicity: DeclarationPeriodicity;
}

// ============================================================================
// FAMILY
// ============================================================================

export interface Family {
  familyId: string;
  memberNo: string;
  parent1: ParentInfo;
  parent2?: ParentInfo;
  childrenIds: string[];

  // Extended fields from enrollment
  emergencyContacts?: EmergencyContact[];  // Контакти за спешни случаи (до 2)
  enrollmentStatus?: EnrollmentStatus;     // Статус на записването
  enrollmentId?: string;                   // Връзка към Enrollment документа

  metadata: {
    createdAt: Timestamp;
    updatedAt: Timestamp;
    createdBy: string;
    source: 'import' | 'manual' | 'form' | 'enrollment';
    // Lifecycle fields
    isActive?: boolean;
    deactivatedAt?: Timestamp;
    deactivatedBy?: string;
    deactivationReason?: string;
    // GDPR erasure fields
    gdprErased?: boolean;
    gdprErasedAt?: Timestamp;
    gdprErasedBy?: string;
    gdprRequestReference?: string;
    // Profile completeness (PR #3c) — true once required parent + child
    // extended fields are populated. Set by onboardParentCore after invitation
    // completion via /enroll-complete, or by updateFamilyProfile (PR #4).
    // Drives the ProfileCompletionBanner on parent dashboard.
    profileComplete?: boolean;
    profileCompletedAt?: Timestamp;
  };
}

export interface ParentInfo {
  name: string;
  email: string;
  phone: string;
  userId?: string; // Firebase Auth UID
  preferredLanguage?: Language;

  /**
   * Адресите, които този родител е ползвал ПРЕДИ текущия — историята на смените.
   *
   * 🔴 Защо съществува. GDPR износът и заличаването търсят кореспонденцията по
   * `communications.parentEmail`, а то НЕ се обновява при смяна на имейл (и не бива —
   * записът казва на кой адрес е отишло писмото). Тоест без история старата кореспонденция
   * изчезва от износа по чл. 15 и заличаването по чл. 17 в момента на смяната, тихо.
   * Четецът е `адресиНаРодител` (`functions/src/utils/gdprCommQuery.ts`) — правилото живее
   * само там и двата четеца го викат.
   *
   * ⚠️ ПИСАЧ още няма: полето се пълни от задача 33 (`changeParentEmail`). Днес не
   * съществува в нито един документ и четецът намира само текущия адрес — това е
   * състояние, не дефект.
   * 📌 Измерено на production 18.09.2026: 0 смени на имейл досега (няма функция, с която да
   * се направи), максимум 2 РАЗЛИЧНИ адреса на семейство, тоест таванът на Firestore `in`
   * (30) е далеч.
   */
  previousEmails?: string[];

  // Extended fields from enrollment form
  title?: string;                     // Титла (г-н, г-жа, др.)
  firstName?: string;                 // Собствено име
  middleName?: string;                // Презиме
  lastName?: string;                  // Фамилия
  birthDate?: Timestamp;              // Рождена дата
  birthPlace?: string;                // Място на раждане

  address?: {                         // Адрес в Австрия
    street: string;
    city: string;
    postalCode: string;
  };
}

export interface EmergencyContact {
  name: string;
  phone: string;
  relationship?: string;              // Роднинска връзка
}

// ============================================================================
// CHILD
// ============================================================================

export interface Child {
  childId: string;
  familyId: string;
  memberNo: string;
  childName: string;
  schoolYear: string;
  grade: string;
  primaryParentEmail: string;
  secondaryParentEmail?: string;
  assignmentsSummary: {
    total: number;
    pending: number;
    signed: number;
    declined: number;
    lastUpdated: Timestamp;
  };

  // Extended fields from enrollment form
  childNameBulgarian?: string;        // Пълно име на български
  firstName?: string;                 // Собствено име
  middleName?: string;                // Презиме
  lastName?: string;                  // Фамилия
  birthDate?: Timestamp;              // Рождена дата
  birthPlace?: string;                // Място на раждане
  egn?: string;                       // ЕГН (единен граждански номер)

  subjects?: {
    core: string[];                   // Основни предмети
    elective: string[];               // СИП (свободно избираеми)
  };

  preferredDay?: string;              // Предпочитан ден (Събота/Неделя)

  austrianSchool?: {                  // Австрийско училище
    name: string;
    address: string;
    grade: string;
  };

  healthInfo?: {                      // Здравна информация
    allergies?: string;
    conditions?: string;
    notes?: string;
  };

  metadata: {
    createdAt: Timestamp;
    updatedAt: Timestamp;
    isActive: boolean;
  };
}

// ============================================================================
// DECLARATION
// ============================================================================

export interface Declaration {
  declarationId: string;
  code: string;
  /**
   * Версия на СЪДЪРЖАНИЕТО — монотонно число, вдигано САМО от
   * `publishDeclarationVersion`.
   *
   * 🔴 ЛИПСВАЩА версия значи **1**, не „неверсионирана": днешното съдържание Е
   * първата версия (решение Р-Е.10 на owner-а). Чете се през
   * `declarationVersionOf` от `shared/utils/declarationVersion.ts` — никъде
   * другаде `?? 1`.
   *
   * ⚠️ НЕ се вдига при редакция на съдържание. Админът решава кога текстът става
   * нова версия, защото поправка на запетая не бива да прави нова версия за 467
   * семейства. Тоест съдържание, редактирано без публикуване, остава на същата
   * версия — съзнателно приета дупка, не пропуск.
   */
  version?: number;
  title: Record<Language, string>;
  description: Record<Language, string>;
  required: boolean;
  active: boolean;
  periodicity: DeclarationPeriodicity;
  /** GDPR legal basis for the processing this declaration authorizes.
   *  Falls back to DEFAULT_LEGAL_BASIS[code] when absent (legacy docs). */
  legalBasis?: GdprLegalBasis;
  templateUrl?: string;
  formFieldName?: string;
  /** Interactive fields schema (optional - for declarations with dynamic input) */
  fieldsSchema?: {
    version: number;
    fields: import('./interactiveFields').FieldConfig[];
    updatedAt: Timestamp;
    updatedBy: string;
  };
  metadata: {
    createdAt: Timestamp;
    updatedAt: Timestamp;
    /**
     * @deprecated НЕ го чети — типът лъже за данните.
     *
     * 🔴 Обявено е `number` и НЕ е опционално, а в живите шест декларации го
     * НЯМА. Тоест `declaration.metadata.version` минава компилация и връща
     * `undefined` по време на изпълнение. Нула четци и нула писачи в целия репо
     * (проверено 15.09.2026) — тоест досега никой не е попадал на лъжата.
     *
     * Версията на съдържанието е `Declaration.version` (горе), чете се през
     * `declarationVersionOf`. Това поле остава само защото махането му е
     * промяна на схема, а не на код — прави се, когато някой чисти типовете.
     *
     * ⚠️ И е ТРЕТОТО място за версия в този документ: другото мъртво е
     * `fieldsSchema.version`, хардкоднато на 1 при всяко записване, също с нула
     * четци.
     */
    version?: number;
  };
}

// ============================================================================
// ASSIGNMENT
// ============================================================================

export interface Assignment {
  assignmentId: string;
  childId: string;
  familyId: string; // Denormalized for queries
  declarationId: string;
  // Denormalized fields
  childName: string;
  declarationCode: string;
  declarationTitle: Record<Language, string>;
  parentEmail: string;
  schoolYear: string;
  status: AssignmentStatus;
  dueDate: Timestamp;
  signatureId?: string | null;
  method?: 'digital' | 'google_form' | 'paper' | null;
  fileUrl?: string | null;
  reminders: {
    count: number;
    lastSentAt?: Timestamp;
    nextReminderAt?: Timestamp;
  };
  metadata: {
    createdAt: Timestamp;
    lastStatusChangeAt: Timestamp;
    updatedAt: Timestamp;
  };
}

// ============================================================================
// LEGAL BINDING (Bilingual declarations — German as legally valid version)
// ============================================================================

export interface LegalBinding {
  contentLanguage: 'bg' | 'de' | 'en';
  germanContentSnapshot: string;
  selectedContentSnapshot: string;
  /**
   * Заглавието, което е стояло над текста при подписването — на избрания език.
   *
   * 🔴 ОПЦИОНАЛНО, и остава такова: 1434-те подписа отпреди 16.09.2026 не го
   * носят, защото поле за него не е съществувало. Направено задължително, то би
   * счупило четенето на всеки заварен подпис; попълнено назад от живата
   * декларация, би твърдяло, че знаем заглавие, което никой не е записал.
   * Липсата се ЧЕТЕ — документът изписва изричен ред, че заглавието е текущото
   * (`resolveSignedDocumentSource`, `titleSnapshotMissing`).
   *
   * ⚠️ Отделно от `selectedContentSnapshot` и НЕ потиска версията: иначе задача
   * 16 (номерът на подписаната версия) щеше да е мъртва за всички стари подписи.
   */
  selectedTitleSnapshot?: string;
  /**
   * Немското заглавие при подписването — правно обвързващата половина.
   *
   * Отделен низ от `selectedTitleSnapshot`, защото документът рисува ДВЕ
   * заглавия (`pdfService`: `declarationTitle` и `germanDeclarationTitle`) и
   * едното снимано без другото дава документ, чиито две половини описват
   * различни моменти.
   */
  germanTitleSnapshot?: string;
  legalDisclaimerAccepted: boolean;
  legalDisclaimerAcceptedAt: string; // ISO timestamp
  disclaimerText: string;
}

// ============================================================================
// SIGNATURE
// ============================================================================

export interface Signature {
  signatureId: string;
  assignmentId: string;
  /**
   * Версията на декларацията, която родителят е ВИДЯЛ, когато е подписал.
   *
   * 🔴 ЛИПСВАЩА версия значи **НЕ СЕ ЗНАЕ** — и остава така. Това е обратното
   * на `Declaration.version`, където липсата значи 1.
   *
   * Една и съща липса, противоположни значения: за декларация тя е „първата
   * версия", за подпис — „нямаме представа на какво се е подписал". Старите
   * подписи НЕ получават 1 назад: приписването изглежда като чистене, а е
   * фалшификация — твърди, че знаем нещо, което не знаем.
   *
   * Чете се през `signedVersionOf`; той никога не подразбира.
   */
  declarationVersion?: number;
  /**
   * Кога е била публикувана ТАЗИ версия. Снима се при подписването, защото на
   * декларацията полето е едно и се презаписва при всяко публикуване — тоест
   * след версия 4 датата на версия 3 не съществува никъде.
   *
   * Вярна по конструкция: подпис се приема само когато показаната версия е
   * равна на живата, тоест живата дата в този момент Е датата на тази версия.
   * Чете се през `signedVersionPublishedMillis`; липсата значи „не се знае".
   */
  declarationVersionPublishedAt?: Timestamp;
  parentEmail: string;
  parentName: string;
  parentUserId: string;
  responseType: ResponseType;
  responseAt: Timestamp;
  signatureData: {
    type: SignatureType;
    imageUrl?: string;
    verificationCode?: string | null;
    ipAddress: string;
    userAgent: string;
    geolocation?: {
      lat: number;
      lng: number;
    };
  };
  /** Interactive fields values (optional - filled by parent during signing) */
  fieldValues?: import('./interactiveFields').SignatureFieldValues;

  // ─────────────────────────────────────────────────────────────────────────
  // Typed declaration data (structured for queries and reporting)
  // ─────────────────────────────────────────────────────────────────────────
  /** GDPR consent structured data */
  gdprData?: import('./declarationData').GdprConsentData;
  /** Photo consent structured data */
  photoConsent?: import('./declarationData').PhotoConsentData;
  /** Pickup authorization structured data */
  pickupAuth?: import('./declarationData').PickupAuthData;
  /** School rules acknowledgment structured data */
  schoolRules?: import('./declarationData').SchoolRulesData;
  /** Medical information structured data */
  medicalInfo?: import('./declarationData').MedicalInfoData;
  /** Emergency contacts structured data */
  emergencyContacts?: import('./declarationData').EmergencyContactsData;

  /**

   * 🔴 ПРОПУСК с ограничен живот, НЕ адрес.

   *

   * Подписан URL към Cloud Storage, издаден в момента на подписването с

   * `PDF_URL_EXPIRY_HOURS` (24ч на production). След това връща

   * `ExpiredToken` — XML, не документ.

   *

   * ⚠️ Не го показвай на потребител и не го препращай. Трима четци вече го

   * прочетоха като адрес; последният случай е `CellDetailsModal`, поправен

   * 2026-09-11. За гледане на екран искай ПРЕСЕН пропуск през

   * `getDeclarationDocumentUrl` (15 минути + одит).

   */

  documentUrl?: string;
  isEffective: boolean;
  comments?: string;
  gdprConsent: {
    dataProcessing: boolean;
    emailCommunication: boolean;
    photoUse: boolean;
    /**
     * GDPR Art. 8 — explicit parental consent flag, required when the data
     * subject (child) is under 16. Present on ALL signatures for audit
     * consistency; `true` for minors, `null`/absent for adults.
     */
    parentalConsent?: boolean;
    /**
     * Snapshot of the child's age at the moment of consent. Proves that the
     * consent was lawfully required (minor) or lawfully collected (adult).
     * Undefined if child birthDate is unknown.
     */
    childAgeAtConsent?: number;
    /**
     * ISO date string (YYYY-MM-DD) of the child's birthDate at consent time.
     * Captured for audit, independent of later edits to Child.birthDate.
     */
    childBirthDateAtConsent?: string;
    /** GDPR legal basis snapshot from the Declaration document at time of signing. */
    legalBasis?: GdprLegalBasis;
    consentGivenAt: Timestamp;
    consentVersion: string;
    ipAddress: string;
    userAgent: string;
  };
  /** Bilingual legal binding data (German as legally valid version) */
  legalBinding?: LegalBinding;
  /**
   * GDPR Art. 7(3) — consent revocation. Present only on signatures
   * whose consent has been withdrawn by the parent after signing.
   */
  revocation?: {
    revokedAt: Timestamp;
    revokedBy: string;
    reason?: string;
    ipAddress?: string;
  };
  metadata: {
    createdAt: Timestamp;
    updatedAt: Timestamp;
  };
}

// ============================================================================
// COMMUNICATION
// ============================================================================

export interface Communication {
  commId: string;
  assignmentId?: string | null;
  parentEmail: string;
  channel: CommunicationChannel;
  templateCode: string;
  subject: string;
  body: string;
  sentAt: Timestamp;
  deliveryStatus: DeliveryStatus;
  messageId?: string;
  errorMessage?: string | null;
  metadata: {
    createdAt: Timestamp;
  };
}

// ============================================================================
// PICKUP PHOTO — Optional photo supplement for PICKUP_AUTH declaration
// ============================================================================
// Photos are stored separately from the signed declaration so the declaration
// remains immutable. Each photo links to a specific authorized person via
// signatureId + personIndex (0, 1, or 2 — matching AuthorizedPerson position).

export interface PickupPhoto {
  pickupPhotoId: string;        // Generated ID with prefix "PUP-"
  signatureId: string;          // Links to signed PICKUP_AUTH declaration
  childId: string;              // Denormalized for teacher queries
  familyId: string;             // Denormalized for security rules
  personIndex: 0 | 1 | 2;       // Which authorized person (0=first, 1=second, 2=third)
  personName: string;           // Denormalized for verification (matches AuthorizedPerson.name)
  photoUrl: string;             // Cloud Storage signed URL (long-lived)
  storagePath: string;          // For deletion: pickup-photos/{schoolId}/{familyId}/{pickupPhotoId}.jpg
  metadata: {
    uploadedAt: Timestamp;
    uploadedBy: string;         // userId of uploader (parent)
  };
}

// Request/response shapes for pickup photo functions

export interface UploadPickupPhotoRequest {
  signatureId: string;
  personIndex: 0 | 1 | 2;
  personName: string;           // Must match AuthorizedPerson.name in declaration
  imageBase64: string;          // Compressed JPEG, max ~200KB
  mimeType: 'image/jpeg' | 'image/png' | 'image/webp';
}

export interface UploadPickupPhotoResponse {
  success: boolean;
  pickupPhotoId: string;
  photoUrl: string;
}

export interface DeletePickupPhotoRequest {
  pickupPhotoId: string;
}

export interface DeletePickupPhotoResponse {
  success: boolean;
}

export interface ListPickupPhotosRequest {
  signatureId?: string;         // List for one declaration
  childId?: string;             // OR list for all child's declarations
}

export interface ListPickupPhotosResponse {
  photos: PickupPhoto[];
}

// ============================================================================
// USER
// ============================================================================

export interface User {
  userId: string; // Firebase Auth UID
  email: string;
  displayName: string;
  role: UserRole;
  schoolId: string;
  familyId?: string;
  /**
   * Езикът на ИНТЕРФЕЙСА за този потребител.
   *
   * 🔴 Стои на ВЪРХА на документа, не във `profile` отдолу — така го пише
   * екранът и точно него разрешават правилата
   * (`users`: `hasOnly([… 'preferredLanguage' …])`). Полето под `profile`
   * съществува в този тип от по-рано и НИКОЙ не го пише и не го чете; не се
   * маха тук, за да не се пипа тип извън обхвата на задачата, но не е то.
   *
   * ⚠️ И НЕ е същото като `ParentInfo.preferredLanguage` в семейния документ —
   * онова командва езика на ПИСМАТА. Двете се държат в синхрон от
   * `setPreferredLanguage`; преди нея бяха несвързани.
   */
  preferredLanguage?: Language;
  profile: {
    phone?: string;
    preferredLanguage: Language;
    notificationSettings: {
      email: boolean;
      sms: boolean;
      push: boolean;
    };
  };
  metadata: {
    createdAt: Timestamp;
    lastLoginAt: Timestamp;
  };
}

// ============================================================================
// INVITATION TOKEN
// ============================================================================

export interface InvitationToken {
  tokenId: string;
  token: string; // UUID v4
  schoolId: string; // Added for direct lookup without N+1 queries
  familyId: string;
  memberNo: string;
  sentTo: string[]; // email addresses
  createdAt: Timestamp;
  expiresAt: Timestamp;
  usedAt?: Timestamp;
  usedBy?: string; // userId who used it
  isValid: boolean;
}

// ============================================================================
// CUSTOM CLAIMS (Firebase Auth)
// ============================================================================

export interface CustomClaims {
  role: UserRole;
  roles?: UserRole[];       // Multi-role support (e.g., ['admin', 'teacher'])
  schoolId: string;
  familyId?: string;
  teacherId?: string;       // Teacher document ID
  email?: string;
}

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================

export interface CreateSignatureRequest {
  assignmentId: string;
  responseType: ResponseType;
  /**
   * Версията на декларацията, която екранът е ПОКАЗАЛ на родителя.
   *
   * 🔴 Сървърът НЕ я взима от живия документ: това би записало версията при
   * ЧЕТЕНЕ, а не тази, която родителят е видял — тоест подпис, твърдящ съгласие
   * за текст, който не е бил на екрана.
   *
   * ⚠️ Но не се приема и на доверие: сървърът я сверява срещу живата версия и
   * отказва при разминаване (админ е публикувал, докато модалът е бил отворен).
   * По-добре родителят да презареди, отколкото подписът да лъже.
   *
   * Липсва при СТАР bundle (service worker сервира стария клиент неопределено
   * дълго). Тогава подписът остава БЕЗ версия — честният запис, вместо
   * подразбиране.
   */
  declarationVersion?: number;
  signatureData: {
    type: SignatureType;
    imageData?: string; // Base64 encoded image
    verificationCode?: string | null; // For code-based signatures
  };
  comments?: string;
  gdprConsent: {
    dataProcessing: boolean;
    emailCommunication: boolean;
    photoUse: boolean;
    /** GDPR Art. 8 — parental consent flag, required when child is < 16 */
    parentalConsent?: boolean;
    /** Child's age at moment of consent (snapshot for audit) */
    childAgeAtConsent?: number;
    /** Child's birthDate snapshot (YYYY-MM-DD) at moment of consent */
    childBirthDateAtConsent?: string;
    /** GDPR legal basis snapshot from the Declaration document at time of signing. */
    legalBasis?: GdprLegalBasis;
  };
  /** Interactive field values (if declaration has fieldsSchema) */
  fieldValues?: import('./interactiveFields').SignatureFieldValues;
  /** Bilingual legal binding data (German as legally valid version) */
  legalBinding?: LegalBinding;
}

export interface CreateSignatureResponse {
  signatureId: string;
  documentUrl: string;
  success: boolean;
  error?: string;
}

export interface SendRemindersRequest {
  schoolId: string;
  dryRun?: boolean;
  /**
   * Таван на ПОСТАВЕНИТЕ в опашката писма за този пробег. По подразбиране 250.
   *
   * ⚠️ „Поставени", не „излезли" — писмото минава през опашка с ограничение по
   * темп (`medium`: 1 на 250 сек) и гард, който я спира при 420 за деня. Тоест
   * при таван 250 последните няколко рутинно излизат на другия ден. Това не е
   * повреда: напомнянията не са времекритични.
   */
  maxToSend?: number;
}

export interface SendRemindersResponse {
  /** Обходени записи — НЕ е „годни", а „погледнати". */
  processed: number;
  /**
   * РЕАЛНО изпратени писма. 🔴 При `dryRun` е 0 — броят отива в `wouldSend`.
   * Дотук `dryRun` вдигаше `sent`, тоест отчетът казваше „изпратени" за нула
   * изпратени, и измерването лъжеше точно за числото, което мери.
   */
  sent: number;
  /** Биха били изпратени при `dryRun`. Извън него е 0. */
  wouldSend: number;
  /**
   * Сборът на четирите причини долу. Заварено име, запазено нарочно: махането
   * му е промяна в договора без полза, а сборът е полезен сам по себе си.
   */
  skipped: number;
  /** Още не им е дошло времето (интервалът не е изтекъл). */
  skippedInterval: number;
  /** Неактивно или изтрито по GDPR дете/семейство. */
  skippedIneligible: number;
  /** Липсващ или невалиден адрес на родителя. */
  skippedInvalidEmail: number;
  /** Липсваща декларация или счупена връзка към данните. */
  skippedMissingData: number;
  failed: number;
  /**
   * Дневният таван на ИЗПРАТЕНИТЕ е стигнат и пробегът е спрян по него.
   * ⚠️ Не е грешка: остатъкът се поема от следващия пробег.
   */
  capReached: boolean;
  errors: string[];
}

export interface ExportUserDataRequest {
  userId: string;
}

export interface ExportUserDataResponse {
  downloadUrl: string;
  expiresAt: Timestamp;
}

export interface DeleteUserDataRequest {
  userId: string;
  confirmation: string;
}

export interface DeleteUserDataResponse {
  success: boolean;
  deletedAt: Timestamp;
}

export interface SendInvitationRequest {
  memberNo: string;
  sendToParent1?: boolean;
  sendToParent2?: boolean;
}

export interface SendInvitationResponse {
  success: boolean;
  tokenId: string;
  sentTo: string[];
  expiresAt: Timestamp;
}

/**
 * Extended parent profile fields (optional in onboarding payload).
 * When present, populated into Family.parentN.* during onboardParentCore.
 */
export interface OnboardParentExtendedParent {
  title?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  birthDate?: string;          // ISO 8601 date string; parsed to Timestamp in backend
  birthPlace?: string;
  phone?: string;
  address?: {
    street: string;
    city: string;
    postalCode: string;
  };
}

/**
 * Extended child fields (optional in onboarding payload).
 * Each entry must reference an existing childId in family.childrenIds.
 */
export interface OnboardParentExtendedChild {
  childId: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  /**
   * Full Cyrillic name, written as one field. Sent by the onboarding core
   * screen, which asks for the name the way it is stored (not split into
   * first/last). When both this and firstName+lastName arrive, this wins —
   * it is what the parent actually typed.
   */
  childName?: string;
  childNameBulgarian?: string; // full name in Latin script
  egn?: string;
  birthDate?: string;          // ISO 8601 date string
  birthPlace?: string;
  austrianSchool?: {
    name: string;
    address: string;
    grade: string;
  };
  subjects?: {
    core: string[];
    elective: string[];
  };
  preferredDay?: string;
  healthInfo?: {
    allergies?: string;
    conditions?: string;
    notes?: string;
  };
}

export interface OnboardParentRequest {
  token: string;
  email: string;
  password: string;
  name: string;
  preferredLanguage?: Language;

  // Extended onboarding data (PR #3a — all optional, backwards-compatible).
  // When omitted, onboardParentCore behaves exactly as before.
  parentExtended?: OnboardParentExtendedParent;
  /**
   * Family-scoped address (2026-08-31). Always written to `parent1.address`,
   * whichever parent is activating — the address belongs to the family, and
   * the enrollment form has no address field for parent2 at all. Without this
   * the second parent's address would land in the wrong slot.
   *
   * Partial by design: the onboarding screen asks only for the part that is
   * missing, and the backend merges per key (`parent1.address.city`) so the
   * parts it already has are never overwritten with blanks.
   */
  familyAddress?: {
    street?: string;
    city?: string;
    postalCode?: string;
  };
  emergencyContacts?: EmergencyContact[];
  childrenExtended?: OnboardParentExtendedChild[];
}

export interface OnboardParentResponse {
  success: boolean;
  userId: string;
  familyId: string;
}

/**
 * Payload for editing the OTHER parent's slot (parent self-service, бъг 3).
 * Same extended shape as own-parent edits, plus email — which the backend
 * only applies when the target slot has NO userId (an unregistered parent).
 * Changing the email of a registered parent would desync Firebase Auth
 * identity from the family doc, so that path is admin-only.
 */
export interface UpdateOtherParentPayload extends OnboardParentExtendedParent {
  email?: string;
}

/**
 * Parent self-service profile update (PR #4). Re-uses the same extended-data
 * shapes as OnboardParentRequest. All fields optional — parent can update
 * their own data, the other parent's data, emergency contacts, and any of
 * the family's children.
 *
 * Backend enforces:
 *  - caller is authenticated parent (allowedRoles: ['parent'])
 *  - caller's claims.familyId matches the family being updated
 *  - parentExtended writes target the parent matching auth.uid (parent1 or
 *    parent2); otherParent writes target the opposite slot
 *  - otherParent.email applied only when the target slot has no userId
 *  - childrenExtended[].childId must be in family.childrenIds (Set check)
 *  - emergencyContacts: explicit array replaces the list (empty array clears)
 */
export interface UpdateFamilyProfileRequest {
  parentExtended?: OnboardParentExtendedParent;
  otherParent?: UpdateOtherParentPayload;
  emergencyContacts?: EmergencyContact[];
  childrenExtended?: OnboardParentExtendedChild[];
}

export interface UpdateFamilyProfileResponse {
  success: boolean;
  profileComplete: boolean;
}

/**
 * Admin full-edit of any family's details (/admin/families, 2026-06-05).
 * Same payload shapes + validation pipeline as the parent self-service path
 * (EGN validation, derived name sync, email guard for registered parents).
 * Class membership is NOT here — use moveChildToClass.
 */
export interface AdminUpdateFamilyProfileRequest {
  familyId: string;
  parent1?: UpdateOtherParentPayload;
  parent2?: UpdateOtherParentPayload;
  emergencyContacts?: EmergencyContact[];
  childrenExtended?: OnboardParentExtendedChild[];
}

// ============================================================================
// FRONTEND SPECIFIC TYPES
// ============================================================================

export interface DashboardStats {
  totalChildren: number;
  totalAssignments: number;
  pendingAssignments: number;
  signedAssignments: number;
  declinedAssignments: number;
  completionRate: number;
}

export interface AssignmentWithDetails extends Assignment {
  declaration: Declaration;
  child: Child;
  signature?: Signature;
}

export interface FamilyDashboard {
  family: Family;
  children: Child[];
  assignments: AssignmentWithDetails[];
  communications: Communication[];
  stats: DashboardStats;
}

// ============================================================================
// ENROLLMENT (Public enrollment form)
// ============================================================================

/** Data submitted through the public enrollment form */
export interface EnrollmentData {
  // Parent 1 (required)
  parent1: {
    title?: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    birthDate?: Date | Timestamp;
    birthPlace?: string;
    email: string;
    phone: string;
    address: {
      street: string;
      city: string;
      postalCode: string;
    };
  };

  // Parent 2 (optional)
  parent2?: {
    title?: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    birthDate?: Date | Timestamp;
    birthPlace?: string;
    email?: string;
    phone?: string;
    address?: {
      street: string;
      city: string;
      postalCode: string;
    };
  };

  // Emergency contacts (up to 2)
  emergencyContacts: EmergencyContact[];

  // Children (1 or more)
  children: {
    firstName: string;
    middleName?: string;
    lastName: string;
    childNameBulgarian?: string;
    birthDate: Date | Timestamp;
    birthPlace?: string;
    egn?: string;
    grade: string;
    subjects?: {
      core: string[];
      elective: string[];
    };
    preferredDay?: string;
    austrianSchool?: {
      name: string;
      address: string;
      grade: string;
    };
    healthInfo?: {
      allergies?: string;
      conditions?: string;
      notes?: string;
    };
  }[];

  // Declarations/consents
  declarations: {
    [declarationCode: string]: boolean;
  };

  // Preferred language
  preferredLanguage: Language;
}

/** Enrollment type: new family or add child to existing */
export type EnrollmentType = 'new-family' | 'add-child';

// ============================================================================
// CHILD HUB — parent-facing callables (getChildSchedule / hasEmergencyCard)
// ============================================================================

export interface GetChildScheduleRequest {
  childId: string;
}

export interface ChildScheduleSlotInfo {
  /** Canonical lowercase-English day (e.g. 'saturday') */
  dayOfWeek: string;
  startTime: string;
  endTime?: string;
  subjects: string[];
  teachers: string[];
}

export interface GetChildScheduleResponse {
  slots: ChildScheduleSlotInfo[];
  className?: string;
  /**
   * Същите часове, но ЗА КАЛЕНДАРА — един запис на СЛОТ, неслепени.
   *
   * 🔴 Защо втори масив, а не полета в `slots`: `slots` са ГРУПИРАНИ по ден и
   * начален час (`childSchedule.ts` ги слива, за да покаже „математика + музика
   * в 9:00"). Планът обаче е на слот — колко занятия има курсът и откога брои.
   * Слепени, два слота с различен план искат правило „кой печели", което никой
   * не е решавал: максимумът обещава занятия на приключил курс, минимумът крие
   * реални. Затова календарът получава суровите слотове, а екраните със списък
   * запазват групираните.
   *
   * ⚠️ Типът е `TeacherScheduleSlotView` НАРОЧНО, макар да е за дете: това е
   * входният контракт на `buildTeacherCalendarEvents`. Паралелен тип със същите
   * полета се разминава при първата промяна — а тогава родителският календар
   * тихо започва да смята друго от учителския.
   *
   * `isAssistant` е винаги `false` — понятието е на учителя и стои само защото
   * типът е общ.
   */
  calendarSlots?: TeacherScheduleSlotView[];
}

export interface HasEmergencyCardRequest {
  childId: string;
}

export interface HasEmergencyCardResponse {
  /** True if emergencyCards/{childId} document exists. No medical data exposed. */
  exists: boolean;
  /**
   * Флаговете на писача (`syncEmergencyCard`): коя ЧАСТ на картата е попълнена, без съдържанието ѝ.
   * Липсват при `exists: false` и при карта без полето — четецът тогава третира частта като
   * НАЛИЧНА (днешното поведение). ⚠️ Копие на типа има в `functions/src/triggers/childSchedule.ts`;
   * двете се менят заедно.
   */
  hasMedicalData?: boolean;
  hasEmergencyContacts?: boolean;
  /**
   * Отворените искания на родителя към училището за това дете (задача 39Б) — по едно на код.
   * Кутията решава САМА (със `isParentRequestLive` от `shared/utils/emergencyBoxState`) дали
   * искането още чака: сървърът е източникът на факта, че то съществува, клиентът не чете
   * колекцията. Липсва при стар бекенд — тогава кутията просто не показва „искане изпратено".
   */
  openRequests?: Array<{ declarationCode: string; requestedAtMillis: number }>;
  /**
   * Кои кодове родителят може да поиска СЕГА — решено от СЪРВЪРА със същия резолвер, с който
   * `requestFromSchool` приема искането (задача 39Б). Кутията показва бутона само по това поле и
   * не го извежда наново от своите данни: клиентът чете възлаганията по семейство с подредба,
   * сървърът — по дете, и разминаване би направило бутона лъжлив. Липсва при празно и при стар бекенд.
   */
  requestable?: Array<{ declarationCode: string; state: ParentRequestState }>;
  /**
   * Исканията, които училището ЗАТВОРИ БЕЗ ДЕЙСТВИЕ, за кодове, които родителят пак може да поиска
   * (задача 39Б) — и САМО докато искането още описва сегашното положение на кода
   * (`isDismissedRequestCurrent`: същото възлагане · същото състояние · нищо по кода след
   * искането). Иначе известие от предишен цикъл изплува над нов отказ. Кутията казва кога и защо
   * и предлага ново искане. Документът е един на код и новото искане го презаписва.
   * 🔴 `reason` е свободният текст на админа (≤ `PARENT_REQUEST_REASON_MAX_LENGTH`) — модалът го
   * обявява като видим за родителите. Липсва при празно и при стар бекенд.
   */
  dismissedRequests?: Array<{ declarationCode: string; dismissedAtMillis: number; reason: string }>;
}

// ── Искане на родител към училището (задача 39Б) ─────────────────────────────

/** Затворено ли е искането и как. `resolved` = училището е изпълнило; `dismissed` = без действие. */
export type ParentRequestStatus = 'open' | 'resolved' | 'dismissed';

/**
 * С какво е затворено: върната за попълване · възложена · затворена без действие · остаряла
 * (действие по искане, което вече не описва сегашното положение — училището е действало отдругаде).
 */
export type ParentRequestResolutionAction = 'reopened' | 'assigned' | 'dismissed' | 'stale';

/**
 * Машинният код в съобщението на грешката (412), когато действие от панела намери искането
 * остаряло. Искането е затворено като `stale`; панелът показва преведен текст и се презарежда.
 */
export const PARENT_REQUEST_STALE = 'parent-request-stale';

/**
 * Таванът на причината при „Затвори без действие" (задача 39Б): тя се рисува в кутията на
 * родителя (`hasEmergencyCard.dismissedRequests`). Сървърът отказва над него (400), модалът в
 * панела не позволява да се напише повече — една константа за двете страни.
 * ⚠️ Единиците са UTF-16 (`String.length`, както и `maxlength`): кирилицата е 1 на буква, емоджи — 2.
 */
export const PARENT_REQUEST_REASON_MAX_LENGTH = 500;

/**
 * Машинният код в съобщението на грешката (400), когато причината е над тавана. Панелът показва
 * преведен текст — стар клиент без `maxLength` или браузър, който брои знаците по-иначе.
 */
export const PARENT_REQUEST_REASON_TOO_LONG = 'parent-request-reason-too-long';

/** Състоянието на кода в момента на искането — само тези три позволяват искане. */
export type ParentRequestState = 'declined' | 'revoked' | 'notAssigned';

/**
 * Документът `schools/{id}/parentRequests/{childId}_{code}`. САМО за сървъра (`firestore.rules`).
 *
 * Идентификаторът е детерминиран: най-много едно искане на дете и код. Затворено искане се
 * презаписва от следващото — историята му е в одита и в действието, което го е изпълнило.
 */
export interface ParentRequest {
  requestId: string;
  childId: string;
  familyId: string;
  declarationCode: string;
  stateAtRequest: ParentRequestState;
  /** Възлагането зад отказа / оттеглянето. `null` при `notAssigned`. */
  assignmentId: string | null;
  requestedByUserId: string;
  /** Имейлът на викащия — сверен като регистриран родител на детето (`matchParentSlot`). */
  requestedFromEmail: string;
  requestedFromSlot: 'parent1' | 'parent2';
  requestedAt: unknown; // Firestore Timestamp
  status: ParentRequestStatus;
  resolution: {
    action: ParentRequestResolutionAction;
    byUserId: string;
    at: unknown; // Firestore Timestamp
    reason: string | null;
    assignmentId: string | null;
  } | null;
}

/** Клиентът праща САМО детето — кои кодове могат да се поискат решава сървърът. */
export interface RequestFromSchoolRequest {
  childId: string;
}

export interface RequestFromSchoolResponse {
  /** По един ред на код, който е поискан (нов или вече отворен). */
  requests: Array<{ declarationCode: string; requestedAtMillis: number; created: boolean }>;
  /** Тръгна ли писмото до училището. `false` и когато нищо ново не е създадено. */
  emailSent: boolean;
}

/** Един ред в панела „Искания от родители" в матрицата. */
export interface ParentRequestListItem {
  requestId: string;
  childId: string;
  childName: string;
  familyId: string;
  declarationCode: string;
  /** За действието „Възложи" (`batchAssignDeclarations` иска `declarationId`, не код). */
  declarationId: string | null;
  /**
   * Състоянието на кода — по него се избира действието. Списъкът съдържа само ЖИВИ искания
   * (`isParentRequestLive`), тоест сегашното състояние е по дефиниция същото като при искането.
   */
  stateAtRequest: ParentRequestState;
  /** Възлагането зад отказа / оттеглянето — целта на „Върни за попълване". `null` при `notAssigned`. */
  assignmentId: string | null;
  requestedAtMillis: number;
  requestedFromEmail: string;
  requesterName: string;
}

export interface ListParentRequestsResponse {
  requests: ParentRequestListItem[];
}

export interface DismissParentRequestRequest {
  requestId: string;
  /** Задължителна: затваряне без действие е „не" към родителя. */
  reason: string;
}

export interface DismissParentRequestResponse {
  requestId: string;
}

/** Enrollment document stored in Firestore */
export interface Enrollment {
  enrollmentId: string;
  schoolId: string;
  schoolYear: string;

  // Enrollment type
  type: EnrollmentType;                // 'new-family' (default) or 'add-child'
  existingFamilyId?: string;           // Set when type === 'add-child'
  existingMemberNo?: string;           // Displayed to admin for confirmation

  // MemberNo management
  tempMemberNo: string;               // TEMP-2025-001 (generated at submission)
  permanentMemberNo?: string;         // 157 (assigned after approval)

  // Status
  status: EnrollmentStatus;

  // Form data
  formData: EnrollmentData;

  // References (created after approval)
  familyId?: string;
  userId?: string;                    // Firebase Auth UID of parent1

  // Timestamps and audit
  submittedAt: Timestamp;
  approvedAt?: Timestamp;
  approvedBy?: string;                // Admin userId
  approvalId?: string;                // Unique per approval request — lets a retried commit recognise its own write (approveEnrollment)
  rejectedAt?: Timestamp;
  rejectedBy?: string;
  rejectionReason?: string;

  metadata: {
    createdAt: Timestamp;
    updatedAt: Timestamp;
    ipAddress?: string;
    userAgent?: string;
  };
}

/** Request to submit an enrollment */
export interface SubmitEnrollmentRequest {
  schoolId: string;
  formData: EnrollmentData;
  existingFamilyId?: string;
}

/** Response from enrollment submission */
export interface SubmitEnrollmentResponse {
  success: boolean;
  enrollmentId: string;
  tempMemberNo: string;
  error?: string;
}

/** Request to approve an enrollment */
export interface ApproveEnrollmentRequest {
  enrollmentId: string;
  permanentMemberNo: string;          // Admin can override suggested number
  editedFormData?: EnrollmentData;    // Full corrected form data (if admin made edits)
  classAssignments?: {
    childIndex: number;
    classId: string;
  }[];
  addToFamilyId?: string;             // Admin-selected existing family (from duplicate detection)
}

/** Response from enrollment approval */
export interface ApproveEnrollmentResponse {
  success: boolean;
  familyId: string;
  permanentMemberNo: string;
  error?: string;
  /**
   * SoT Phase 1: runtime class-assignment failures AFTER the approve batch
   * committed (children exist but are not linked to a class). Pre-commit
   * validation rejects bad assignments outright; this only carries
   * unexpected post-commit failures so the admin can fix membership via
   * the data-integrity tools instead of the failure being swallowed.
   */
  classAssignmentWarnings?: string[];
}

/** Request to reject an enrollment */
export interface RejectEnrollmentRequest {
  enrollmentId: string;
  reason: string;
}

/** Response from enrollment rejection */
export interface RejectEnrollmentResponse {
  success: boolean;
  error?: string;
}

/** Request to check if a family already exists by email or memberNo */
export interface CheckExistingFamilyRequest {
  email: string;
  memberNo?: string;
}

/** Response from existing family check (minimal data for privacy) */
export interface CheckExistingFamilyResponse {
  exists: boolean;
  familyId?: string;
  memberNo?: string;
  parentName?: string;           // First name only for confirmation
  childrenCount?: number;
}

/** Request to add a child to an existing family (parent-initiated) */
export interface AddChildRequest {
  childData: Record<string, unknown>;  // Dynamic fields from formConfig
}

/** Response from add child request */
export interface AddChildResponse {
  success: boolean;
  enrollmentId: string;          // Pending enrollment created
  error?: string;
}

/** Request to find potential duplicate families for an enrollment */
export interface FindDuplicateFamiliesRequest {
  enrollmentId: string;
}

/** A potential duplicate family match */
export interface DuplicateFamilyMatch {
  familyId: string;
  memberNo: string;
  parentName: string;
  childrenCount: number;
  matchReasons: string[];        // e.g. ['parent2_email', 'child_lastname', 'phone']
}

/** Response from duplicate family search */
export interface FindDuplicateFamiliesResponse {
  duplicates: DuplicateFamilyMatch[];
}

// ============================================================================
// SCHOOL SETTINGS
// ============================================================================

/** Request for getting school settings */
export interface GetSchoolSettingsRequest {
  schoolId?: string;
}

/** Response for getting school settings */
export interface GetSchoolSettingsResponse {
  school: School | null;
}

/** Request for updating school settings */
export interface UpdateSchoolSettingsRequest {
  name?: {
    bg?: string;
    de?: string;
    en?: string;
  };
  location?: {
    city?: string;
    country?: string;
    address?: string;
  };
  contact?: SchoolContact;
  branding?: Partial<Omit<SchoolBranding, 'updatedAt' | 'updatedBy'>>;
  /** Правни/банкови данни за официалните PDF-и (school.legal.*). */
  legal?: Partial<Omit<SchoolLegal, 'updatedAt' | 'updatedBy'>>;
  autoAssignDeclarations?: boolean;
  /**
   * Data Protection Officer contact. Pass `null` to clear the DPO
   * (not recommended — GDPR Art. 37 may require one).
   */
  dpo?: Omit<DpoContact, 'updatedAt' | 'updatedBy'> | null;
}

/** Response for updating school settings */
export interface UpdateSchoolSettingsResponse {
  success: boolean;
  school: School;
}

/** Asset types for upload */
export type SchoolAssetType =
  | 'logo'
  | 'logoLight'
  | 'favicon'
  | 'pdfHeader'
  | 'pdfFooter'
  | 'emailHeader';

/** Request for uploading school asset */
export interface UploadSchoolAssetRequest {
  assetType: SchoolAssetType;
  base64Data: string;
  mimeType: string;
  fileName?: string;
}

/** Response for uploading school asset */
export interface UploadSchoolAssetResponse {
  success: boolean;
  url: string;
  assetType: SchoolAssetType;
}

/** Request for deleting school asset */
export interface DeleteSchoolAssetRequest {
  assetType: SchoolAssetType;
}

/** Response for deleting school asset */
export interface DeleteSchoolAssetResponse {
  success: boolean;
}

// ============================================================================
// REPORT EXPORT
// ============================================================================

/** Report types that can be exported */
export type ReportType =
  | 'declarations'      // Declaration statistics
  | 'families'          // Family list with children
  | 'assignments'       // Assignment status overview
  | 'fees'              // Fee payments status
  | 'children';         // Children by grade

/** Export format */
export type ExportFormat = 'excel' | 'pdf';

/** Request for exporting a report */
export interface ExportReportRequest {
  reportType: ReportType;
  format: ExportFormat;
  filters?: {
    dateFrom?: string;          // ISO date string
    dateTo?: string;            // ISO date string
    status?: string;            // Filter by status
    declarationId?: string;     // Filter by declaration
    grade?: string;             // Filter by grade
  };
  /**
   * GDPR Art. 5(1)(c) — pseudonymization toggle. When true, replaces
   * personal names with memberNo in the export output. Useful for sharing
   * statistics with third parties without exposing PII.
   */
  pseudonymized?: boolean;
}

/** Response from report export */
export interface ExportReportResponse {
  success: boolean;
  downloadUrl: string;
  fileName: string;
  expiresAt: Timestamp;
  error?: string;
}

// ============================================================================
// FORM BUILDER (Enrollment Form Configuration)
// ============================================================================

/** Field types supported by the form builder */
export type FormFieldType =
  | 'text'
  | 'email'
  | 'phone'
  | 'date'
  | 'select'
  | 'multiselect'
  | 'checkbox'
  | 'textarea'
  | 'number';

/** Section types for organizing form fields */
export type FormSectionType = 'parent' | 'child' | 'emergency' | 'custom';

/** Validation rules for form fields */
export interface FieldValidation {
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  customValidator?: 'egn' | 'phone' | 'email';
}

/** Option for select/multiselect fields */
export interface FieldOption {
  value: string;
  label: Record<Language, string>;
}

/** Conditional display rule */
export interface ConditionalRule {
  fieldId: string;
  operator: 'equals' | 'notEquals' | 'contains' | 'isEmpty' | 'isNotEmpty';
  value?: string;
}

/** Single form field configuration */
export interface FormFieldConfig {
  id: string;
  type: FormFieldType;
  label: Record<Language, string>;
  placeholder?: Record<Language, string>;
  required: boolean;
  validation?: FieldValidation;
  options?: FieldOption[];
  conditionalOn?: ConditionalRule;
  order: number;
  defaultValue?: string;
  helpText?: Record<Language, string>;
}

/** Form section containing multiple fields */
export interface FormSectionConfig {
  id: string;
  title: Record<Language, string>;
  description?: Record<Language, string>;
  type: FormSectionType;
  fields: FormFieldConfig[];
  repeatable: boolean;
  minItems?: number;
  maxItems?: number;
  order: number;
}

/** Subject configuration for enrollment */
export interface FormSubjectConfig {
  code: string;
  name: Record<Language, string>;
  type: 'core' | 'elective';
}

/** Declaration/consent item for the form */
export interface FormDeclarationConfig {
  id: string;
  code: string;
  text: Record<Language, string>;
  required: boolean;
  linkUrl?: string;
  linkText?: Record<Language, string>;
  order: number;
}

/** Complete enrollment form configuration */
export interface EnrollmentFormConfig {
  schoolId: string;
  schoolYear: string;
  isActive: boolean;
  version: number;

  // Form structure
  sections: FormSectionConfig[];
  declarations: FormDeclarationConfig[];

  // Settings
  settings: {
    allowMultipleChildren: boolean;
    maxChildren: number;
    requireSecondParent: boolean;
    requireEmergencyContact: boolean;
    minEmergencyContacts: number;
    maxEmergencyContacts: number;
    availableGrades: string[];
    availableDays: string[];
    availableSubjects: FormSubjectConfig[];
  };

  // Metadata
  metadata: {
    createdAt: Timestamp;
    createdBy: string;
    updatedAt: Timestamp;
    updatedBy: string;
    publishedAt?: Timestamp;
    publishedBy?: string;
  };
}

/** Request to get form config */
export interface GetFormConfigRequest {
  schoolId?: string;
  schoolYear?: string;
}

/**
 * One option for the public "Клас" dropdown — derived from the real `classes`
 * collection (NOT the manual `availableGrades`). Either a standard grade level
 * (deduped — "1-ви клас", not 1А/1Б) or a named special group (ПГ "Моливко" 1…).
 */
export interface EnrollmentClassOption {
  /** Stored in child.grade: "1" for a level, the class name for a special group. */
  value: string;
  /** Set → standard level (UI formats "N-ти клас"); null → special group (use `value` as label). */
  gradeLevel: number | null;
}

/** Response with form config */
export interface GetFormConfigResponse {
  config: EnrollmentFormConfig | null;
  isDefault: boolean;
  /**
   * Class options for the public dropdown, computed from active `classes` of the
   * resolved school year (PII-safe — names/levels only). Empty = no classes set
   * up → enrollment not possible. Optional for backward compat with old clients.
   */
  availableClasses?: EnrollmentClassOption[];
}

/** Request to save form config */
export interface SaveFormConfigRequest {
  config: Omit<EnrollmentFormConfig, 'metadata'>;
  publish?: boolean;
}

/** Response from saving form config */
export interface SaveFormConfigResponse {
  success: boolean;
  version: number;
  error?: string;
}

/** Request to get default form config template */
export interface GetDefaultFormConfigRequest {
  schoolYear: string;
}

/** Response with default form config */
export interface GetDefaultFormConfigResponse {
  config: EnrollmentFormConfig;
}

// ============================================================================
// INTERACTIVE FIELDS (Re-exported from interactiveFields.ts)
// ============================================================================

export * from './interactiveFields';

// ============================================================================
// FEE MANAGEMENT (Re-exported from fees.ts)
// ============================================================================

export * from './fees';

// ============================================================================
// DECLARATION DATA (Re-exported from declarationData.ts)
// ============================================================================

export * from './declarationData';

// ============================================================================
// AUDIT LOG
// ============================================================================

/** Audit log action types */
export type AuditAction =
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'SIGN'
  | 'DECLINE'
  | 'SEND_INVITATION'
  | 'SEND_REMINDER'
  | 'IMPORT'
  | 'EXPORT'
  | 'LOGIN'
  | 'LOGOUT'
  | 'APPROVE'
  | 'REJECT'
  | 'PAYMENT_SUBMIT'
  | 'PAYMENT_CONFIRM'
  | 'PAYMENT_REJECT'
  | 'SEND_VOLUNTEER_INVITATIONS'
  | 'VIEW_MEDICAL_DATA'
  | 'VIEW_EMERGENCY_DATA'
  | 'VIEW_PARENT_CONTACTS'
  | 'EXPORT_CLASS_DATA'
  | 'SEND_TEACHER_INVITATION'
  | 'ASSIGN_TEACHER'
  | 'EXPENSE_SUBMIT'
  | 'EXPENSE_APPROVE'
  | 'EXPENSE_REJECT'
  | 'ADVANCE_ISSUE'
  | 'ADVANCE_CLOSE'
  | 'ADD_CHILD_REQUEST'
  | 'ADD_CHILD_APPROVE'
  | 'FAMILY_DEACTIVATED'
  /**
   * Смяна на имейла на родител (задача 33, `changeParentEmail`).
   *
   * 🔴 Одитът е УСЛОВИЕ за правото, не добавка: функцията е достъпна за `admin`, не само за
   * `superadmin`, и обосновката за това е, че superadmin-only би изтласкал операцията в
   * конзолата — тоест ръчно и БЕЗ следа. Право без следа е по-лошо от тясно право.
   * ⚠️ Записът носи СТАРИЯ и новия адрес: той е единственото място, където се вижда кой е
   * сменил идентификатора за вход на човек.
   */
  | 'PARENT_EMAIL_CHANGED'
  | 'FAMILY_REACTIVATED'
  | 'FAMILY_DELETED'
  | 'GDPR_DATA_ERASED'
  | 'TEST_ACCOUNT_DELETED'
  | 'ORPHAN_FAMILY_DELETED'
  | 'CHILD_NAME_CLEANED'
  | 'UPDATE_FAMILY_PROFILE'
  /** Админ върна декларация за повторно попълване (`reopenDeclaration`). */
  | 'DECLARATION_REOPENED'
  /**
   * Админ обяви текущото съдържание за НОВА версия
   * (`publishDeclarationVersion`).
   *
   * Одитира се, защото всеки подпис СЛЕД това реферира именно този номер: при
   * спор въпросът „на какъв текст се е подписал родителят" се свежда до „кой,
   * кога и какво е обявил за версия N".
   */
  | 'DECLARATION_VERSION_PUBLISHED'
  /**
   * Админ отвори ПОДПИСАНИЯ документ (`getDeclarationDocumentUrl`).
   *
   * Документът носи ръкописен подпис и — при `MEDICAL_INFO` — здравни данни.
   * Дотук това беше единственото действие в екрана БЕЗ следа, докато
   * връщането и спешният достъп се одитират.
   */
  | 'VIEW_DECLARATION_DOCUMENT'
  // Масово преизграждане на спешните карти (задача 38). Масова операция върху криптирани
  // медицински данни — одитът е УСЛОВИЕ за правото да се пуска, не добавка.
  | 'EMERGENCY_CARDS_REBUILT';

/** Resource types that can be audited */
export type AuditResourceType =
  | 'family'
  | 'child'
  | 'assignment'
  | 'signature'
  | 'declaration'
  | 'invitation'
  | 'enrollment'
  | 'payment'
  | 'fee'
  | 'settings'
  | 'user'
  | 'volunteer'
  | 'teacher'
  | 'class'
  | 'scheduleSlot'
  | 'emergencyCard'
  | 'expense'
  | 'advance'
  | 'recipient'
  | 'pickupPhoto'
  | 'room'
  | 'venuesConfig'
  | 'holiday'
  | 'category'
  | 'rearrangeJob'
  | 'hoursSubmission'
  | 'plannedAssignment'
  | 'lohnzettel';

/** Audit log entry */
export interface AuditLog {
  logId: string;
  schoolId: string;
  timestamp: Timestamp;

  // Who performed the action
  userId: string;
  userEmail: string;
  userRole: 'admin' | 'parent' | 'teacher' | 'system';

  // What action was performed
  action: AuditAction;

  // On what resource
  resourceType: AuditResourceType;
  resourceId: string;
  resourceName?: string; // Human-readable name (e.g., child name, family memberNo)

  // Additional details
  details?: Record<string, any>;

  // Request metadata
  ipAddress?: string;
  userAgent?: string;
}

/** Request for listing audit logs */
export interface ListAuditLogsRequest {
  schoolId?: string;
  filters?: {
    dateFrom?: string;
    dateTo?: string;
    action?: AuditAction;
    resourceType?: AuditResourceType;
    userId?: string;
  };
  pagination?: {
    limit: number;
    startAfter?: string; // logId for cursor pagination
  };
}

/** Response for listing audit logs */
export interface ListAuditLogsResponse {
  logs: AuditLog[];
  hasMore: boolean;
  nextCursor?: string;
}

// ============================================================================
// IMPORT VALIDATION CONFIG
// ============================================================================

/** Configuration for import validation rules */
export interface ImportValidationConfig {
  schoolId: string;

  // Parent fields
  parent1: {
    nameRequired: boolean;
    emailRequired: boolean;
    phoneRequired: boolean;
  };

  parent2: {
    enabled: boolean;        // Whether to expect Parent 2
    nameRequired: boolean;   // If enabled, whether name is required
    emailRequired: boolean;
    phoneRequired: boolean;
  };

  // Children
  children: {
    minCount: number;        // Minimum number of children (usually 1)
    maxCount: number;        // Maximum number of children (usually 10)
    nameRequired: boolean;
    gradeRequired: boolean;
  };

  // Email validation
  emailValidation: {
    allowDuplicates: boolean;  // One email for multiple families
    requireUnique: boolean;    // Each email must be unique in the system
  };

  // Metadata
  updatedAt: Timestamp;
  updatedBy: string;
}

/** Request for saving import config */
export interface SaveImportConfigRequest {
  config: Omit<ImportValidationConfig, 'schoolId' | 'updatedAt' | 'updatedBy'>;
}

/** Response for getting import config */
export interface GetImportConfigResponse {
  config: ImportValidationConfig | null;
}

/** Teacher import validation config */
export interface TeacherImportValidationConfig {
  schoolId: string;
  phoneRequired: boolean;
  contractTypeRequired: boolean;
  hoursRequired: boolean;
  classesRequired: boolean;
  emailValidation: {
    requireUnique: boolean;
  };
  updatedAt: Timestamp;
  updatedBy: string;
}

/** Request for saving teacher import config */
export interface SaveTeacherImportConfigRequest {
  config: Omit<TeacherImportValidationConfig, 'schoolId' | 'updatedAt' | 'updatedBy'>;
}

/** Response for getting teacher import config */
export interface GetTeacherImportConfigResponse {
  config: TeacherImportValidationConfig | null;
}

// ============================================================================
// VOLUNTEERS (Re-exported from volunteers.ts)
// ============================================================================

export * from './volunteers';

// ============================================================================
// EXPENSES & ADVANCES (Re-exported from expenses.ts)
// ============================================================================

export * from './expenses';

// ============================================================================
// TEACHER (Module 5)
// ============================================================================

export type TeacherStatus = 'active' | 'inactive';

export type ContractType = 'freier_dienstvertrag' | 'geringfuegig' | 'other';

export type ScheduleCategory = 'ДГ' | 'ПГ' | '1-4' | '5-8_БЕЛ' | '9-12';

/** Ролята на учителя в назначението. Липсва → преподавател (сегашното
 *  поведение на всички съществуващи документи; полето е добавено 2026-09-01
 *  и нищо не се мигрира).
 *
 *  `assistant` = помощен учител: придружава класа, но НЕ води час. Затова
 *  такова назначение съзнателно НЕ участва нито в атрибуцията на слот към
 *  учител (`resolveSlotTeacher`), нито в проверката за двойно резервиране
 *  (`slotConflictTeacherIds`) — иначе помощникът или би „откраднал" слота от
 *  преподавателя (стъпка 2/3 връщат ПЪРВИЯ кандидат), или би вдигнал
 *  фалшив конфликт за час, който двамата водят заедно по замисъл. */
export type TeacherAssignmentRole = 'assistant';

export interface TeacherAssignment {
  classId: string;
  scheduleSlotId?: string;
  category?: ScheduleCategory;
  hoursPerWeek?: number;
  /** Липсва = преподавател. Виж TeacherAssignmentRole. */
  role?: TeacherAssignmentRole;
}

export interface Teacher {
  teacherId: string;
  userId?: string;
  name: string;
  email: string;
  phone?: string;
  status: TeacherStatus;
  contractType?: ContractType;
  contractedHoursPerYear?: number;
  assignments: TeacherAssignment[];
  startDate?: Timestamp;
  endDate?: Timestamp;
  /**
   * Класове, в които учителят е ПОМОЩНИК (Module 9, 2026-09-02).
   *
   * Отделно поле, а НЕ `role: 'assistant'` в `assignments`: последните са
   * ИЗВЕДЕН ИНДЕКС от слотовете (`teacherAssignmentsIndex.ts:40` строи само
   * `{classId, scheduleSlotId}`) и се преизчисляват при всеки запис на слот —
   * ръчно сложена роля би изчезнала тихо, а заплатата би се върнала на
   * титулярска ставка без следа защо.
   *
   * Помощникът е извън атрибуцията на слот по дизайн, тоест може да няма
   * собствен слот в класа, който придружава — затова списъкът е ръчен.
   */
  assistantClassIds?: string[];
  deactivatedAt?: Timestamp;
  deactivatedReason?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;

  // Module 8 (Phase 3) — algorithm inputs. All optional за backward compat:
  // existing teacher docs work без re-migration; new fields populated when
  // admin opens AdminTeachersPage (Phase 3) и попълни qualifications.
  /** FK array → CategoryDefinition.categoryId. Source of truth за what kind of
   *  groups this teacher can run. Replaces the legacy `qualifiedGradeRanges`
   *  enum array (5 coarse values) — registry has 18+ free-text categories. */
  qualifiedCategoryIds?: string[];
  /** DEPRECATED — kept for backward compat during Phase 3 transition. New
   *  algorithm code reads qualifiedCategoryIds. */
  qualifiedSubjects?: string[];
  /** DEPRECATED — see qualifiedCategoryIds. */
  qualifiedGradeRanges?: ScheduleCategory[];
  /** Days/times when teacher is available. Empty/undefined = no constraint
   *  (algorithm assumes all-day available). */
  availability?: import('./module8').TimeBlock[];

  // Module 9 (Phase 1) — payroll. Optional за backward compat.
  /** IBAN за банковото разнасяне на заплати. Смяната е guarded операция
   *  (одит + потвърждение + пауза на авто-match) — вж. spec Design hardening. */
  iban?: string;
  ibanUpdatedAt?: Timestamp;
  ibanUpdatedBy?: string;
  /** Деривиран payroll статус per учебна година.
   *  Key = schoolYearKey ("2025-2026"). Вж. TeacherPayrollYear (module9). */
  payrollByYear?: Record<string, import('./module9').TeacherPayrollYear>;
  /**
   * Периодите на договора (TASK_47) — СУРОВИ: типът е `unknown` нарочно. Админ може да пише
   * `teachers/{id}` и от клиента (firestore.rules), тоест стойността не е доказано валидна; чете се
   * САМО през `parseContractPeriods` / `resolveMonthContract` (К1). Пише ги `setContractPeriods`.
   */
  contractPeriods?: unknown;
  /** Оптимистична версия на периодите — `setContractPeriods` иска `expectedVersion`. */
  contractPeriodsVersion?: number;
  /** Lohnzettel reconciliation (Module 9 LZ1/LZ2) — Pnr от фиша (напр.
   *  "75003"). Записва се при първия потвърден PDF import match → следващите
   *  import-и match-ват по Pnr. */
  providerPersonnelNr?: string;
}

// ============================================================================
// CLASS (Module 5)
// ============================================================================

export type ClassStatus = 'active' | 'inactive';

export type SchoolClassType = 'main' | 'sip';

export interface SchoolClass {
  classId: string;
  name: string;
  grade: number;
  section: string;
  schoolYear: string;
  childIds: string[];
  status: ClassStatus;
  createdAt: Timestamp;
  updatedAt: Timestamp;

  // Module 8 (Phase 2A) — FK to CategoryDefinition registry (new source of
  // truth для category-specific data: academicHoursPerWeek, weeksPerYear).
  // Optional за backward compat — legacy classes still use the `category`
  // ScheduleCategory enum below. New classes get both fields (legacy enum
  // auto-derived from CategoryDefinition.groupingTag).
  categoryId?: string;

  // Module 8 (Phase 3) — algorithm inputs. All optional за backward compat.
  /** "main" = redovny block, "sip" = свободно избираема дейност.
   *  Default behavior (when undefined) is treated as 'main'. */
  type?: SchoolClassType;
  /** Седмичен общ брой часове за този клас. Main: 2 или 3. SIP: variable. */
  weeklyDuration?: number;
  /** Locked-in slot fields. Set when admin override-ва algorithm-ското
   *  предложение или ръчно фиксира group time/place. */
  weeklyDayOfWeek?: import('./module8').DayOfWeek;
  weeklyStartTime?: string;       // "HH:MM"
  weeklyEndTime?: string;         // "HH:MM"
  /** FK → Room.roomId when class is locked to specific room. */
  roomId?: string;
  /** When true, algorithm не може да премести този клас. */
  locked?: boolean;
  lockedBy?: string;              // userId
  lockedAt?: Timestamp;
  /**
   * Module 8 (Phase 5B) — per-dimension rearrange locks. Each locked dimension
   * (room / time = day+startTime / teacher) is held fixed by the rearrange
   * algorithm. Supersedes the coarse `locked` above (which froze the whole
   * class). A `room`-locked class is also exempt from the soft "oversized room"
   * penalty — intentional small-class-in-big-room (напр. Дребосъче в Зелена зала).
   * NB: hard over-capacity (big class → small room) STILL flags even when locked.
   */
  locks?: import('./module8').RearrangeLocks;
  /** За type='sip' само — кои main classes присъстват в този SIP. */
  sipParentClassIds?: string[];
}

// ============================================================================
// SCHEDULE SLOT (Module 6 prep - created now to avoid migration)
// ============================================================================

export type ScheduleSlotStatus = 'active' | 'inactive';

export interface ScheduleSlot {
  slotId: string;
  classId: string;
  displayName: string;
  subject: string | null;
  category: ScheduleCategory;
  schedule: {
    dayOfWeek: string;
    startTime: string;
    endTime: string;
  };
  schoolYear: string;
  status: ScheduleSlotStatus;
  createdAt: Timestamp;
  updatedAt: Timestamp;

  // Module 8 (Phase 3) — room booking. Optional за backward compat
  // (existing slots работят без roomId; algorithm fill-ва когато run-не).
  roomId?: string;
  // Module 8 (Phase 3) — FK → CategoryDefinition. Optional. New code reads
  // categoryId; legacy `category` enum stays as fallback during transition.
  categoryId?: string;
  /** Съзнателен escape от квалификационния гейт при ЗАПИС на този слот —
   *  учителят не е квалифициран за категорията, но админът е поел решението
   *  с причина. Отсъствието му при неквалифициран учител значи заварен запис
   *  отпреди гейта (виж §Съществуващите записи). */
  unqualifiedOverride?: UnqualifiedOverrideRecord;
  /**
   * ОБЯВЕНИ учебни дни за ТОЗИ слот (C4b R0) — ISO дати, на които занятието се провежда
   * ВЪПРЕКИ ваканция. До ~10 на учебна година (owner, 07.09).
   *
   * 🔴 При СЛОТА, не при категорията. Owner, 07.09: „не всички класове от категорията имат
   * липсващо занятие — те са на база конкретен ден, тоест на база слотове" и „за всеки клас
   * поотделно, иначе промяна за една дреболия променя цялата система". Това ОТМЕНЯ решението от
   * 06.09 (изключението при категорията) и отмяната е изрична, за да не бъде „възстановена" като
   * по-простата.
   *
   * Дата извън деня от седмицата на слота няма ефект — обявяването само ОТМЕНЯ пропускането на
   * занятие, което вече е в този ден. Преместване в друг ден е ЗАМЕСТВАЩО занятие (R0б).
   */
  declaredSchoolDays?: string[];
  // Slot-level teacher override (optional). When set, resolveSlotTeacherId
  // attributes THIS slot to this teacher — highest priority, over the
  // class+category heuristic (fixes shared-class first-match ambiguity). A
  // stale/inactive id falls through to the heuristic (never strands the slot).
  teacherId?: string;
}

/**
 * Слотът, какъвто callable-ът за запис го връща на клиента — финалното
 * състояние на документа, изведено ОТ САМИЯ ЗАПИС, не от ново четене.
 *
 * Защо съществува: query-тата на Firestore се обслужват от индекси, които
 * догонват `batch.commit()` асинхронно. Клиент, който прочете списъка веднага
 * след успешен запис, понякога изпреварва индекса и получава стария документ
 * (измерено на staging, 2026-08-26: същият отговор, същият hash). Този payload
 * дава на екрана истината, без да пита базата втори път.
 *
 * Timestamp-носещите полета (`createdAt`, `updatedAt`, `unqualifiedOverride`)
 * нарочно ги няма: през callable сериализацията Timestamp става
 * `{_seconds,_nanoseconds}` — не класът, който Firestore client SDK слага в
 * кеша. Типът щеше да лъже, а рендер с `.toDate()` да гръмне. Вместо тях идва
 * `updatedAtMillis` — обикновено число, което преживява JSON непокътнато.
 */
export type ScheduleSlotWritePayload =
  Omit<ScheduleSlot, 'createdAt' | 'updatedAt' | 'unqualifiedOverride'> & {
    /** Кога сървърът е записал тази версия (epoch ms). Клиентът налага
     *  payload-а само върху ПО-СТАРО прочетено състояние — по-нов чужд запис
     *  оцелява. */
    updatedAtMillis: number;
  };

// ============================================================================
// TEACHER INVITATION TOKEN
// ============================================================================

export interface TeacherInvitationToken {
  tokenId: string;
  token: string;
  schoolId: string;
  teacherId: string;
  email: string;
  createdAt: Timestamp;
  expiresAt: Timestamp;
  usedAt?: Timestamp;
  usedBy?: string;
  isValid: boolean;
  claimedAt?: Timestamp;
  claimedByEmail?: string;
}

// ============================================================================
// TEACHER MANAGEMENT API TYPES
// ============================================================================

export interface CreateTeacherRequest {
  name: string;
  email: string;
  phone?: string;
  contractType?: ContractType;
  contractedHoursPerYear?: number;
  assignments?: TeacherAssignment[];
  /** Module 8 Phase 3 — categories the teacher can run. */
  qualifiedCategoryIds?: string[];
  /** Module 8 Phase 3 — availability windows. */
  availability?: import('./module8').TimeBlock[];
}

export interface CreateTeacherResponse {
  success: boolean;
  teacherId: string;
}

export interface UpdateTeacherRequest {
  teacherId: string;
  name?: string;
  email?: string;
  phone?: string;
  status?: TeacherStatus;
  contractType?: ContractType;
  contractedHoursPerYear?: number;
  assignments?: TeacherAssignment[];
  deactivatedReason?: string;
  /** Module 8 Phase 3 — categories the teacher can run. Empty array clears. */
  qualifiedCategoryIds?: string[];
  /** Module 8 Phase 3 — availability windows. Empty array clears (no constraint). */
  availability?: import('./module8').TimeBlock[];
  /** Module 9 — IBAN за заплата (админски път; mod-97 валидиран). Празен string чисти. */
  iban?: string;
  /**
   * Начало на работа — ISO "YYYY-MM-DD". Вход за отстъпката „нов учител"
   * (`isNewTeacherAsOf`: първата година от тази дата).
   *
   * Празен string или null ИЗТРИВА полето — тогава учителят не се брои за нов
   * и взима пълната ставка (текущото поведение за всички, които нямат дата).
   * Неподадено поле оставя съществуващата стойност непокътната.
   */
  startDate?: string | null;
  /**
   * Класове, в които учителят е помощник. Празен масив ЧИСТИ списъка;
   * неподадено поле оставя съществуващия непокътнат.
   */
  assistantClassIds?: string[];
}

// Module 9 — IBAN self-service (updateMyIban; идентичност от token-а).
export interface UpdateMyIbanRequest {
  /** Нормализира се server-side; празно/null = изчистване. */
  iban: string | null;
}

export interface UpdateMyIbanResponse {
  success: boolean;
  iban: string | null;
}

export interface UpdateTeacherResponse {
  success: boolean;
}

export interface DeleteTeacherRequest {
  teacherId: string;
}

export interface DeleteTeacherResponse {
  success: boolean;
}

export interface ListTeachersRequest {}

export interface ListTeachersResponse {
  teachers: Teacher[];
}

export interface SendTeacherInvitationRequest {
  teacherId: string;
}

export interface SendTeacherInvitationResponse {
  success: boolean;
  tokenId: string;
  sentTo: string;
  expiresAt: Timestamp;
}

export interface OnboardTeacherRequest {
  token: string;
  email: string;
  password: string;
  name: string;
  preferredLanguage?: Language;
}

export interface OnboardTeacherResponse {
  success: boolean;
  userId: string;
  teacherId: string;
}

// ============================================================================
// CLASS MANAGEMENT API TYPES
// ============================================================================

export interface CreateClassRequest {
  name: string;
  grade: number;
  section: string;
  schoolYear: string;
  /** Module 8 Phase 2A — FK to CategoryDefinition.categoryId. Optional за
   *  backward compat; new classes should provide it. */
  categoryId?: string;
}

export interface CreateClassResponse {
  success: boolean;
  classId: string;
}

export interface UpdateClassRequest {
  classId: string;
  name?: string;
  grade?: number;
  section?: string;
  schoolYear?: string;
  /**
   * @deprecated Пълен списък — праща го само стар bundle (service worker). Сървърът го приема, но
   * пресмята разликата спрямо ТЕКУЩИЯ клас — промяна от друг пишещ след снимката на клиента се губи
   * (TASK_65 Р5). Нов код праща `addChildIds` / `removeChildIds`. Не се комбинира с тях.
   */
  childIds?: string[];
  /**
   * TASK_65: членството по НАМЕРЕНИЕ — само децата, добавени/премахнати от този клиент. Чуждите промени
   * след снимката оцеляват. Подадени (и празни) → сървърът маха от класа и ID-тата без документ дете.
   */
  addChildIds?: string[];
  removeChildIds?: string[];
  status?: ClassStatus;
  /** Module 8 — locked room assignment. Empty/null clears assignment. */
  roomId?: string | null;
  /** Module 8 Phase 2A — FK reassignment. Empty/null clears the FK. */
  categoryId?: string | null;
  /** Module 8 Phase 2b — persistent per-dimension rearrange locks. */
  locks?: import('./module8').RearrangeLocks | null;
}

/**
 * TASK_65: какво е направил сървърът с членството — бройки, без имена. `mode: 'intent'` е доказателството
 * за клиента, че `addChildIds` / `removeChildIds` са приложени (стар сървър не връща полето).
 */
export interface ClassMembershipSummary {
  mode: 'intent' | 'full';
  /** Съществуващи деца, влезли в класа с тази заявка. */
  added: number;
  /** Съществуващи деца, излезли от класа с тази заявка. */
  removed: number;
  /** ID-та без документ дете (в класа или в заявката за запис) — нито едно не е записано. */
  droppedMissing: number;
  /** Децата в класа след записа. */
  childCount: number;
}

export interface UpdateClassResponse {
  success: boolean;
  /** Само когато заявката е пипала членството. */
  membership?: ClassMembershipSummary;
}

/**
 * SoT Phase 1 (дете↔клас): canonical admin action for changing a child's
 * class. Transactional — removes the child from other classes of the same
 * school year, adds to the target, keeps the child.grade hint in sync.
 * targetClassId null/undefined → remove from any class (clears the hint).
 */
export interface MoveChildToClassRequest {
  childId: string;
  targetClassId?: string | null;
  /**
   * Year scope for REMOVAL (targetClassId null) — dual-year: a child can be
   * in one class per school year. Defaults to the child's own year. For an
   * assignment the scope is the target class's year.
   */
  year?: string;
}

export interface MoveChildToClassResponse {
  success: boolean;
  removedFromClassIds: string[];
  addedToClassId: string | null;
  /** The new child.grade hint (class name, '' when removed from class). */
  newGrade: string;
  /** The school year the operation was scoped to (normalized). */
  scopeYear: string;
}

export interface DeleteClassRequest {
  classId: string;
}

export interface DeleteClassResponse {
  success: boolean;
}

export interface ListClassesRequest {}

export interface ListClassesResponse {
  classes: SchoolClass[];
}

/**
 * A class enriched with the metadata shown in child↔class assignment dropdowns:
 * the class's slot (day+time, if scheduled), its teacher (if resolvable), and
 * room capacity vs current enrolment. Computed server-side via the canonical
 * resolvers (effectiveRoomId, scheduleResolution) so consumers never re-derive.
 */
export interface EnrichedClassOption {
  classId: string;
  name: string;
  grade: number;
  schoolYear: string;
  /** "Сб 09:00–11:00" — joined day+time of the class's active slot(s). Empty if none. */
  slotLabel: string;
  /** Resolved teacher name(s), joined. Empty if none/unassigned. */
  teacherName: string;
  /** Room name (when the class is locked to a room). Empty if none. */
  roomName: string;
  /** Current enrolled children (class.childIds length). */
  enrolled: number;
  /** Room capacity for the class's grade band. Undefined when no room is set. */
  capacity?: number;
  /** capacity - enrolled. Undefined when no room is set. */
  freeSpots?: number;
}

export interface ListClassesEnrichedRequest {
  schoolYear: string;
}

export interface ListClassesEnrichedResponse {
  classes: EnrichedClassOption[];
}

// ============================================================================
// SCHEDULE SLOT MANAGEMENT API TYPES
// ============================================================================

export interface CreateScheduleSlotRequest {
  classId: string;
  displayName: string;
  subject: string | null;
  category: ScheduleCategory;
  schedule: { dayOfWeek: string; startTime: string; endTime: string };
  schoolYear: string;
  /** Module 8 Phase 3 — optional room binding at creation. */
  roomId?: string;
  /** Module 8 Phase 3 — optional category FK at creation. */
  categoryId?: string;
  /** Slot-level teacher override at creation (optional). */
  teacherId?: string;
  /** Отключва запис при неквалифициран учител (§Квалификационен гейт). */
  acknowledgeUnqualified?: AcknowledgeUnqualified;
}

export interface CreateScheduleSlotResponse {
  success: boolean;
  slotId: string;
  /** Създаденият слот — за да не се чака четене, което може да изпревари
   *  индекса. Optional: заварен клиент срещу нов сървър и обратно. */
  slot?: ScheduleSlotWritePayload;
}

export interface UpdateScheduleSlotRequest {
  slotId: string;
  classId?: string;
  displayName?: string;
  subject?: string | null;
  category?: ScheduleCategory;
  schedule?: { dayOfWeek: string; startTime: string; endTime: string };
  status?: ScheduleSlotStatus;
  /** Module 8 Phase 3 — room binding. Empty string/null clears. */
  roomId?: string | null;
  /** Module 8 Phase 3 — category FK. Empty string/null clears. */
  categoryId?: string | null;
  /** Slot-level teacher override. Empty string/null clears. */
  teacherId?: string | null;
  /**
   * ОБЯВЕНИ учебни дни за този слот (C4b R0) — ISO дати. Празен списък ги ИЗЧИСТВА.
   *
   * 🔴 Полето трябва да е и в `updateData` на `updateScheduleSlot`, не само тук: строителят там
   * приема САМО познати полета и изхвърля непознатото МЪЛЧАЛИВО (CLAUDE.md — така
   * `assistantClassIds` стоеше празно на 18 от 18 учителя, без нито една грешка).
   */
  declaredSchoolDays?: string[];
  /** Отключва запис при неквалифициран учител (§Квалификационен гейт).
   *  Гейтът се задейства САМО когато update-ът променя учителя или
   *  категорията — не при смяна на час/стая (owner решение 2026-08-24). */
  acknowledgeUnqualified?: AcknowledgeUnqualified;
}

export interface UpdateScheduleSlotResponse {
  success: boolean;
  /** Обновеният слот — виж ScheduleSlotWritePayload. Optional по същата
   *  причина като при create. */
  slot?: ScheduleSlotWritePayload;
}

export interface DeleteScheduleSlotRequest {
  slotId: string;
}

export interface DeleteScheduleSlotResponse {
  success: boolean;
  /**
   * Кога сървърът е изтрил слота (epoch ms, сървърно време).
   *
   * Клиентът маха реда от кеша веднага и после го пази от догонващото четене,
   * което още може да върне изтрития запис (същият race като при редакцията —
   * виж ScheduleSlotWritePayload). Сравнението е „видяният запис е отпреди
   * моето изтриване"; слот, ПРЕСЪЗДАДЕН след този момент, носи по-нов
   * `updatedAt` и оцелява.
   *
   * Optional: заварен клиент срещу нов сървър и обратно.
   */
  deletedAtMillis?: number;
}

export interface ListScheduleSlotsRequest {}

export interface ListScheduleSlotsResponse {
  slots: ScheduleSlot[];
}

// ============================================================================
// COPY CLASSES TO NEW YEAR API TYPES
// ============================================================================

export type CopyClassConflictAction = 'skip' | 'overwrite';

export interface CopyClassesRequest {
  sourceSchoolYear: string;
  targetSchoolYear: string;
  dryRun: boolean;
  /** Per-class edits from preview step (key = source classId) */
  classEdits?: Record<string, {
    name: string;
    grade: number;
    section: string;
    excluded: boolean;
  }>;
  /** Per-class conflict resolution (key = source classId) */
  conflictResolutions?: Record<string, CopyClassConflictAction>;
  /** Phase 2: move children from source to target classes */
  moveChildren?: boolean;
  /** Phase 2: copy teacher assignments to new classes/slots */
  copyTeachers?: boolean;
  /** Phase 2: deactivate source classes after copy */
  deactivateSource?: boolean;
  /** Phase 2: transfer-only mode — skip class/slot creation, match existing target classes */
  transferOnly?: boolean;
  /**
   * When a conflict is resolved as "skip", automatically copy source schedule slots
   * to the target class if (and only if) the target class currently has NO slots.
   * Prevents duplication in target classes that already have slots. Default: true.
   */
  fillMissingSlots?: boolean;
}

export interface CopyClassPreviewItem {
  sourceClassId: string;
  sourceName: string;
  sourceGrade: number;
  sourceSection: string;
  proposedName: string;
  proposedGrade: number;
  proposedSection: string;
  scheduleSlotCount: number;
  conflict: boolean;
  conflictClassId?: string;
  excluded: boolean;
  excludeReason?: string;
  /** Phase 2: count of active children in source class */
  childCount: number;
  /** Phase 2: names of teachers assigned to source class */
  teacherNames: string[];
}

export interface CopyClassesPreview {
  sourceSchoolYear: string;
  targetSchoolYear: string;
  classes: CopyClassPreviewItem[];
  totalClasses: number;
  totalExcluded: number;
  totalConflicts: number;
  totalScheduleSlots: number;
  /** Phase 2: total active children across non-excluded classes */
  totalChildren: number;
  /** Phase 2: distinct teachers with assignments to source classes */
  totalTeachers: number;
  /** Module 8 Phase 2A: CategoryDefinition records in source year — all copied
   *  forward to target year with new categoryIds; SchoolClass.categoryId is
   *  remapped. Zero if source year has no registry yet. */
  totalCategories: number;
}

export interface CopyClassesResponse {
  success: boolean;
  preview?: CopyClassesPreview;
  result?: {
    classesCreated: number;
    classesOverwritten: number;
    classesSkipped: number;
    scheduleSlotsCreated: number;
    /** Phase 2 */
    childrenMoved: number;
    teachersUpdated: number;
    classesDeactivated: number;
    /** Module 8 Phase 2A — new CategoryDefinition records created in target
     *  year (excludes ones that already existed by name — idempotent rerun). */
    categoriesCreated: number;
    errors: string[];
  };
}

// ============================================================================
// TEACHER VIEW API TYPES
// ============================================================================

export interface TeacherAlert {
  type: 'missing_medical' | 'missing_emergency' | 'missing_pickup' | 'missing_photo_consent' | 'missing_gdpr_consent' | 'updated_data';
  childId: string;
  childName: string;
  classId: string;
  className: string;
  message?: string;
}

export interface GetTeacherDashboardRequest {}

export interface TeacherChildListItem {
  childId: string;
  childName: string;
  classId: string;
  className: string;
}

/**
 * Един час на учителя, вече РЕЗОЛВНАТ от сървъра — ден, час, зала и клас.
 *
 * Защо идва от сървъра, а не се смята на клиента: атрибуцията слот→учител е
 * `resolveSlotTeacherId`, който иска ПЪЛНИЯ учителски регистър, а
 * `firestore.rules` дава на учителя само собствения му документ
 * (`teachers/{id}` при `request.auth.token.teacherId == teacherId`). Клиентът
 * физически не може да изпълни канона.
 *
 * Трите клиентски заместителя са измерени срещу production (2026-09-03) и
 * трите грешат — затова полето е сървърно:
 *
 *   resolveSlotTeacherId (сървър)    51/51 активни слота, 0 неатрибутирани
 *   slot.teacherId                   35/51 — съвпада с канона, но 16 са без него
 *   assignments[].scheduleSlotId     35/65 назначения → трима учители с 0 часа
 *   всички слотове на класовете ми   надценява: споделен клас дава 10 вместо 5
 */
export interface TeacherScheduleSlotView {
  slotId: string;
  classId: string;
  className: string;
  displayName: string;
  /** Както е записан в слота ("saturday", "понеделник") — клиентът го превежда. */
  dayOfWeek: string;
  startTime: string;              // "HH:MM"
  endTime: string;                // "HH:MM"
  /** Резолвната през `effectiveRoomId` (слот → клас). null = зала не е зададена;
   *  49 от 51 активни слота имат зала, тоест null е рядък, но реален случай. */
  roomName: string | null;
  /**
   * Сградата и етажът на стаята — ПЪЛНОТО място, когато е известно.
   *
   * ⚠️ По избор и днес се пълнят САМО за родителския календар (`getChildSchedule`).
   * Учителският път ги оставя празни и `slotLabel` не ги ползва — тоест краткият
   * етикет не се удължава. Пълното място се показва при кликване на ден, където
   * има място за него (решение на owner, 16.09.2026).
   */
  venueName?: string | null;
  floor?: string | null;
  /**
   * true = учителят е ПОМОЩНИК в този клас, не титуляр на слота.
   *
   * Помощникът е извън `resolveSlotTeacherId` НАРОЧНО (не води час), затова се
   * добавя изрично от `assistantClassIds`. Без него единственият помощник на
   * production вижда празен календар, макар да ходи на два часа седмично.
   */
  isAssistant: boolean;
  /** FK към `CategoryDefinition`. null = слотът няма категория (легаси запис). */
  categoryId: string | null;
  /**
   * Колко занятия има курсът — `CategoryDefinition.weeksPerYear` на категорията
   * на слота, попълнен СЪРВЪРНО по същата причина като атрибуцията: регистърът
   * на категориите е един, а всеки клиент, който си го извежда сам, се
   * разминава.
   *
   * null = слот без категория или категория без валиден брой. Календарът тогава
   * разгъва до края на прозореца (fail-open) — празният календар е по-лошата
   * грешка от прекалено дългия.
   */
  weeksPerYear: number | null;
  /**
   * РЕШЕНАТА начална дата на занятията (ISO „YYYY-MM-DD"), или `null` при нечетима година.
   *
   * Решава я СЪРВЪРЪТ по същата причина като `weeksPerYear` и атрибуцията: редът на
   * предимство е изключение на категорията → настройките на годината → 1 септември от
   * ключа, а `firestore.rules` не дава на учителя достъп нито до `settings/general`, нито
   * до регистъра на категориите. Клиент, който си я извежда сам, няма входовете — и трите
   * му заместителя грешат.
   */
  startDate: string | null;
  /**
   * ОБЯВЕНИТЕ учебни дни на слота (C4b R0) — датите, на които занятието се провежда ВЪПРЕКИ
   * ваканция.
   *
   * 🔴 Тук, защото иначе календарът и декларацията се разминават. Откакто сметката за часовете
   * ги брои (R1/R2), учител с обявен ден получава очаквани часове за него — а собственият му
   * календар не рисува занятието, тоест той е блокиран за нещо, което не вижда. Докато НИКОЙ
   * не ги броеше, полето беше симетрично инертно и отсъствието му тук беше безобидно.
   *
   * Решава ги СЪРВЪРЪТ и ги подава санирани (`readableDeclaredDays`) — по същата причина като
   * `weeksPerYear` и `startDate`.
   */
  declaredSchoolDays?: string[];
}

/**
 * Клас, в който учителят е ПОМОЩНИК — за карта на таблото.
 *
 * Нарочно НЕ е `SchoolClass` и нарочно е отделно от `classes`: помощникът няма
 * достъп до децата в тези класове (`getClassChildrenData` проверява
 * `teacher.assignments`, а помощничеството живее в отделно поле). Затова тук
 * няма `childIds` — да не изнасяме детски идентификатори към учител, който не
 * може да ги отвори — и картата се рисува БЕЗ линк, вместо да води към 403.
 */
export interface TeacherAssistantClassView {
  classId: string;
  name: string;
  grade: number;
  section: string;
}

/** Неучебен период за календара на учителя. */
export interface TeacherHolidayView {
  holidayId: string;
  name: string;
  type: import('./module8').HolidayType;
  /**
   * ISO "YYYY-MM-DD". Нарочно НЕ Timestamp: callable сериализацията го превръща
   * в `{_seconds}` и всеки консуматор си пише собствен парсер (виж `parseTs` в
   * `AdminPage.tsx`). Низът минава без парсер и не зависи от часова зона.
   */
  startDate: string;
  endDate: string;
}

export interface GetTeacherDashboardResponse {
  teacher: Teacher;
  classes: SchoolClass[];
  alerts: TeacherAlert[];
  childrenList: TeacherChildListItem[];
  totalChildren: number;
  /**
   * Часовете на учителя за активната година. Optional за backward compat:
   * нов клиент срещу СТАР backend получава undefined и показва празен календар,
   * вместо да гърми. Обратната посока (стар клиент, нов backend) игнорира
   * полето — затова редът на deploy тук е безразличен.
   */
  mySlots?: TeacherScheduleSlotView[];
  /** Ваканции/празници за активната година. Optional по същата причина. */
  holidays?: TeacherHolidayView[];
  /**
   * Класовете, в които учителят е ПОМОЩНИК. Отделно от `classes`, защото
   * достъпът е различен: тук няма деца, няма предупреждения и няма линк.
   * Без това поле помощник с 124 договорени часа вижда табло с нула класа.
   */
  assistantClasses?: TeacherAssistantClassView[];
  /**
   * Учебната година, за която са върнати `mySlots` и `holidays` („2026-2027").
   *
   * Клиентът извежда прозореца на календара от НЕЯ, не от часовника си:
   * активната година е съхранена настройка, а календарната се превърта на 1
   * септември независимо от нея. Разминат ли се, прозорецът изрязва всичко
   * върнато и слоят с ваканциите изчезва без грешка. Optional по същата причина
   * като полетата отгоре — стар backend не я връща и клиентът пада на часовника.
   */
  schoolYear?: string;
  /**
   * Границите на календара, РЕШЕНИ на сървъра (C4 R5) — клиентът НЕ ги смята.
   *
   * Optional по същата причина като полетата отгоре: стар backend не ги връща и клиентът пада
   * на прозореца си, вместо да гърми.
   */
  bounds?: TeacherCalendarBounds;
}

export interface ChildDeclarationSummary {
  childId: string;
  childName: string;
  grade: string;
  familyMemberNo: string;
  /**
   * Статусът на ВСЯКА възложена декларация, по код (`GDPR`, `PICKUP_AUTH`, …).
   *
   * Това е носителят на истината за екрана. Булевите полета отдолу свиват
   * четирите състояния до две и точно затова отказът на родителя изглеждаше
   * като „още не е подал" — виж `shared/utils/declarationStatus.ts`.
   *
   * Липсващ ключ = декларацията НЕ е възложена на това дете. Различава се от
   * `'pending'` нарочно: „не е искано" и „искано, но неотговорено" са различни
   * неща за учителя.
   *
   * Optional, защото стар backend не го връща — клиентът тогава пада на
   * булевите полета вместо да гърми.
   */
  declarationStatuses?: Partial<Record<string, AssignmentStatus>>;
  /**
   * @deprecated Ползвай `declarationStatuses`. Извеждат се от него
   * (`status === 'signed'`) и остават САМО за прозореца между двата deploy-а.
   *
   * 🔴 Не се махат в същия commit нарочно. Редът на deploy е `functions` ПЪРВО,
   * а service worker-ът сервира стария bundle на учителя, докато той не натисне
   * „Обнови" в банера — тоест старият екран живее НЕОГРАНИЧЕНО дълго след новия
   * backend. Махнати сега, всички знаци щяха да станат `✗` точно за учителите,
   * заради които се прави поправката.
   */
  hasMedicalInfo: boolean;
  /** @deprecated виж `hasMedicalInfo` */
  hasEmergencyContacts: boolean;
  /** @deprecated виж `hasMedicalInfo` */
  hasPickupAuth: boolean;
  /** @deprecated виж `hasMedicalInfo` */
  hasPhotoConsent: boolean;
  /** @deprecated виж `hasMedicalInfo` */
  hasGdprConsent: boolean;
  /**
   * @deprecated Винаги `false` — не го чети.
   *
   * Извежда се от `gdprData.healthDataProcessing`, което липсва в 197 от 197
   * живи GDPR подписа: декларацията е текст БЕЗ `fieldsSchema`, тоест подписът
   * не носи `fieldValues` и трансформерът не създава `gdprData`.
   *
   * 🔴 И величината зад него не съществува. „Може ли учителят да вижда" вече не
   * е ЕДИН въпрос: медицинските данни минават през emergency endpoint с одит
   * (не влизат в класовия изглед изобщо), а pickup и фото се решават ПО
   * ОПЕРАЦИЯ от `shared/utils/effectiveConsent.ts`.
   *
   * ⚠️ НЕ се маха в същия commit, в който спря да се чете: service worker
   * сервира стария bundle на учителя неопределено дълго, а стар клиент, който
   * не получи полето, се чупи. Маха се, когато никой стар клиент не го чете —
   * същата причина като `hasMedicalInfo` и останалите булеви.
   */
  gdprTeacherConsent: boolean;
  medicalInfo?: import('./declarationData').MedicalInfoData;
  emergencyContacts?: import('./declarationData').EmergencyContactsData;
  pickupAuth?: import('./declarationData').PickupAuthData;
  photoConsent?: import('./declarationData').PhotoConsentData;
}

export interface GetClassChildrenDataRequest {
  classId: string;
}

export interface GetClassChildrenDataResponse {
  classId: string;
  className: string;
  children: ChildDeclarationSummary[];
}

export interface GetChildEmergencyDataRequest {
  childId: string;
}

export interface GetChildEmergencyDataResponse {
  childId: string;
  childName: string;
  className: string;
  emergencyContacts?: import('./declarationData').EmergencyContactsData;
  medicalInfo?: import('./declarationData').MedicalInfoData;
  pickupAuth?: import('./declarationData').PickupAuthData;
}

/**
 * Контакт с ЕДИН родител за едно дете от класа — това, което учителят вижда
 * в таба „Контакт с родител".
 *
 * Умишлено е един родител, не двамата: учителят се обажда на ОСНОВНИЯ, а
 * списък с по два контакта на ред прави таблицата нечетима. Изборът минава
 * през `selectPrimaryParent` (teacherView.ts) — водещ е `child.primaryParentEmail`,
 * не редът в семейството.
 */
export interface ClassParentContact {
  childId: string;
  childName: string;
  grade: string;
  /** Име на родителя по профила; празно, ако семейният запис липсва. */
  parentName: string;
  /** Имейлът, с който родителят е в системата (поканата и входът вървят по него). */
  parentEmail: string;
  /** Телефонът по профила; празен, ако не е попълнен. */
  parentPhone: string;
  /**
   * Има ли този родител реален Auth акаунт (`ParentInfo.userId`).
   * `false` обяснява защо родителят „не отговаря в системата" — още не се е
   * регистрирал по поканата.
   */
  hasAccount: boolean;
}

export interface GetClassParentContactsRequest {
  classId: string;
}

export interface GetClassParentContactsResponse {
  classId: string;
  className: string;
  contacts: ClassParentContact[];
}

// ============================================================================
// TEACHER IMPORT API TYPES
// ============================================================================

export interface ImportTeacherRow {
  name: string;
  email: string;
  phone?: string;
  contractType?: ContractType;
  contractedHoursPerYear?: number;
  classNames?: string[];
}

export interface ImportTeachersResult {
  created: number;
  updated: number;
  skipped: number;
  errors: { row: number; message: string }[];
}

// ============================================================================
// FAMILY LIFECYCLE MANAGEMENT
// ============================================================================

/** Request to deactivate a family (soft delete) */
export interface DeactivateFamilyRequest {
  familyId: string;
  reason?: string;
}

/** Request to reactivate a previously deactivated family */
export interface ReactivateFamilyRequest {
  familyId: string;
}

/** Request to permanently delete a family (hard delete) */
export interface DeleteFamilyRequest {
  familyId: string;
  confirmMemberNo: string;
}

/** Request to anonymize family data per GDPR Article 17 */
export interface GdprEraseFamilyDataRequest {
  familyId: string;
  requestReference?: string;
}

// ============================================================================
// REPORT STATISTICS API TYPES
// ============================================================================

export interface FeeCollectionByYear {
  schoolYear: string;
  totalDue: number;
  totalPaid: number;
  collectionRate: number;
}

export interface EnrollmentTimelineEntry {
  month: string; // YYYY-MM
  count: number;
}

export interface ReportStatisticsResponse {
  feeCollectionByYear: FeeCollectionByYear[];
  enrollmentTimeline: EnrollmentTimelineEntry[];
}

export interface TeacherWorkloadEntry {
  teacherId: string;
  name: string;
  classCount: number;
  childCount: number;
  hoursPerWeek: number;
  daysPerWeek: number;
}

export interface DayLoadEntry {
  dayOfWeek: string;
  totalHours: number;
}

export interface TeacherWorkloadResponse {
  teachers: TeacherWorkloadEntry[];
  dayLoad: DayLoadEntry[];
}

// ============================================================================
// ROLE HELPER
// ============================================================================

/** Check if claims include a specific role (supports both role and roles fields).
 *  superadmin inherits all admin permissions automatically. */
export function hasRole(claims: Partial<CustomClaims> | null | undefined, role: UserRole): boolean {
  if (!claims) return false;
  // superadmin passes all admin checks
  if (role === 'admin' && isSuperAdmin(claims)) return true;
  if (claims.roles?.includes(role)) return true;
  return claims.role === role;
}

/** Check if user has superadmin role */
export function isSuperAdmin(claims: Partial<CustomClaims> | null | undefined): boolean {
  if (!claims) return false;
  if (claims.role === 'superadmin') return true;
  return claims.roles?.includes('superadmin') ?? false;
}

// ============================================================================
// USER / ONBOARDING DATA AUDIT (read-only, superadmin) — cross-references
// Firebase Auth ↔ Firestore to surface leftover test data after go-live.
// Returns metadata only (no password hashes / secrets).
// ============================================================================

/**
 * Признаците, че семейството е ИСТИНСКО, в реда на показване. Измерено 20.10 на production: 35 от 37
 * „несвързани“ семейства (без акаунт за вход) са семейства от импорта с такси, декларации, деца в клас
 * и плащания — родителят просто още не е регистриран. Семейство с поне един признак НЕ се трие от
 * инструментите за изчистване (`cleanupOrphanFamilies`, `cleanupTestAccounts`) — правилото е
 * `familyDataSigns` (functions/src/utils/familyCascade.ts). Типът се извежда ОТТУК — признак извън
 * списъка не съществува.
 */
export const FAMILY_DATA_SIGNS = [
  'payments', 'familyFees', 'signatures', 'assignments',
  'emergencyCards', 'parentRequests', 'childInClass', 'approvedEnrollment',
] as const;

export type FamilyDataSign = typeof FAMILY_DATA_SIGNS[number];

export interface AccountAudit {
  uid: string;
  email: string | null;
  displayName?: string;
  role: string | null;           // from custom claims
  createdAt: string | null;      // ISO
  lastSignInAt: string | null;   // ISO
  emailVerified: boolean;
  disabled: boolean;
  hasUserDoc: boolean;           // users/{uid} Firestore profile exists
  familyId: string | null;
  familyExists: boolean;
  familySource?: string;         // family.metadata.source (import|manual|form|enrollment)
  childrenCount: number;
  enrollmentCount: number;
  feeCount: number;              // familyFees — should be 0 post-wipe
  paymentCount: number;          // payments — should be 0 post-wipe
  /** Признаците на семейството на акаунта ([] без семейство) — с поне един акаунтът не се трие. */
  familyDataSigns: FamilyDataSign[];
}

export interface AuditOrphanFamily {
  familyId: string;
  memberNo?: string;
  source?: string;
  childrenCount: number;
  parentEmails: string[];
  /** [] = несвързано семейство без данни (може да се изтрие); иначе — чака регистрация на родителя. */
  dataSigns: FamilyDataSign[];
}

export interface CleanupAccountPlan {
  uid: string;
  email: string | null;
  status: 'planned' | 'deleted' | 'skipped';
  skipReason?: 'self' | 'superadmin' | 'not-found' | 'has-data';
  /** При skipReason 'has-data' — кои признаци има семейството на акаунта. */
  dataSigns?: FamilyDataSign[];
  familyId: string | null;
  counts: {
    children: number;
    enrollments: number;
    assignments: number;
    signatures: number;
    familyFees: number;
    payments: number;
    invitations: number;
    invitationTokens: number;
    emergencyCards: number;
    parentRequests: number;
    family: number;
    userDoc: number;
    authAccount: number;
  };
}

export interface CleanupTestAccountsRequest {
  uids: string[];
  dryRun?: boolean; // default true
}

export interface CleanupTestAccountsResponse {
  dryRun: boolean;
  accounts: CleanupAccountPlan[];
  totals: { accounts: number; deleted: number; skipped: number; documents: number };
}

/** Per-family plan for cleanupOrphanFamilies (families with NO auth owner). */
export interface CleanupOrphanFamilyPlan {
  familyId: string;
  memberNo?: string;
  status: 'planned' | 'deleted' | 'skipped';
  skipReason?: 'has-owner' | 'not-found' | 'has-data'; // has-owner = a live auth account references it → refuse
  /** При skipReason 'has-data' — кои признаци има семейството (то е истинско → отказ). */
  dataSigns?: FamilyDataSign[];
  counts: {
    children: number;
    enrollments: number;
    assignments: number;
    signatures: number;
    familyFees: number;
    payments: number;
    invitations: number;
    invitationTokens: number;
    emergencyCards: number;
    parentRequests: number;
    family: number;
  };
}

export interface CleanupOrphanFamiliesRequest {
  familyIds: string[];
  dryRun?: boolean; // default true
}

export interface CleanupOrphanFamiliesResponse {
  dryRun: boolean;
  families: CleanupOrphanFamilyPlan[];
  totals: { families: number; deleted: number; skipped: number; documents: number };
}

/** One proposed child-name fix (strip leading list-number etc.). */
export interface ChildNameChange {
  childId: string;
  oldName: string;
  newName: string;
  /** Set when the structured firstName also carries a list-number (2026-06-05). */
  oldFirstName?: string;
  newFirstName?: string;
  memberNo?: string;          // family member number (for orientation)
  residualReasons: string[];  // leftover suspicious bits needing MANUAL review (число/въпросителна/...)
}

export interface CleanupChildNamesRequest {
  dryRun?: boolean;       // default true
  childIds?: string[];    // optional subset; default = all children with a fixable name
}

export interface CleanupChildNamesResponse {
  dryRun: boolean;
  changes: ChildNameChange[];
  total: number;          // number of names that would change
  applied: number;        // number actually written (0 on dry-run)
}

/** One child↔class membership reconciliation step. */
export interface ChildClassReconChange {
  childId: string;
  childName: string;
  grade: string;          // child.grade (the class-name hint)
  childYear: string;
  action: 'add' | 'remove-wrong-year';
  className: string;
  classId: string;
  classYear: string;
}

export interface ChildClassUnmatched {
  childId: string;
  childName: string;
  grade: string;          // grade hint that matched no class for the child's year
  childYear: string;
}

export interface ReconcileChildClassResponse {
  dryRun: boolean;
  changes: ChildClassReconChange[];
  unmatched: ChildClassUnmatched[];   // grade set but no class found for that year — needs manual look
  totals: { add: number; removeWrongYear: number; unmatched: number; applied: number };
}

/** One slot subject/displayName fix derived from a simple-level category. */
export interface SlotNamingChange {
  slotId: string;
  className: string;
  schoolYear: string;
  categoryName: string;
  oldSubject: string;
  newSubject: string;
  oldDisplayName: string;
  newDisplayName: string;
}

export interface MigrateSlotNamingRequest {
  dryRun?: boolean;       // default true
  selected?: string[];    // slotIds to apply; when omitted on apply → all
}

export interface MigrateSlotNamingResponse {
  dryRun: boolean;
  changes: SlotNamingChange[];
  total: number;
  applied: number;
}

/** A confusable-duplicate class pair (e.g. Latin "3A" + Cyrillic "3А"). */
export interface DuplicateClassPair {
  keepClassId: string;     // canonical (more children)
  keepName: string;
  keepChildren: number;
  removeClassId: string;   // to be merged away
  removeName: string;
  removeChildren: number;  // moved into keep
  removeSlots: number;     // re-pointed to keep
  schoolYear: string;
}

export interface MergeDuplicateClassesRequest {
  dryRun?: boolean;
  selected?: string[];     // removeClassIds to merge
}

export interface MergeDuplicateClassesResponse {
  dryRun: boolean;
  pairs: DuplicateClassPair[];
  total: number;
  applied: number;
}

export interface AuditUserDataResponse {
  accounts: AccountAudit[];
  orphanFamilies: AuditOrphanFamily[];               // family with no auth/user-doc owner
  orphanChildren: Array<{ childId: string; familyId: string; childName?: string }>; // child → missing family
  orphanUserDocs: Array<{ uid: string; email: string }>; // users/{uid} with no auth account
  totals: {
    authUsers: number;
    userDocs: number;
    families: number;
    children: number;
    enrollments: number;
    familyFees: number;
    payments: number;
  };
}

// ============================================================================
// МАТРИЦА НА ДЕКЛАРАЦИИТЕ ПО СЕМЕЙСТВО (админ)
// ============================================================================

/**
 * Една клетка от матрицата — състоянието на ЕДНА декларация за ЕДНО дете.
 *
 * Нарочно е лека: за 500 деца × 6 декларации подробностите на подписа са 3000
 * допълнителни четения при всяко отваряне на екрана. Кой е отговорил и къде е
 * документът се дърпат при клик, през `getDeclarationSignatureDetails`.
 */
export interface DeclarationMatrixCell {
  assignmentId: string;
  status: AssignmentStatus;
  /** `metadata.lastStatusChangeAt` в милисекунди (ISO не оцелява през callable). */
  statusChangedAtMillis: number | null;
  /** Крайният срок в милисекунди — за „просрочена и още чака". */
  dueAtMillis: number | null;
  /** Има ли изобщо подпис зад тази клетка (без да го четем). */
  hasSignature: boolean;
}

/** Едно дете в матрицата, с клетките си. */
export interface DeclarationMatrixChild {
  childId: string;
  childName: string;
  /**
   * Класът за активната година, разрешен през `resolveChildClassName` —
   * НЕ суровото `child.grade`. Празен низ, ако детето още не е в клас.
   */
  className: string;
  /** Код на декларация → клетка. Липсващ код = не е възложена. */
  cells: Partial<Record<string, DeclarationMatrixCell>>;
}

/** Един ред „семейство" с децата си под него. */
export interface DeclarationMatrixFamily {
  familyId: string;
  memberNo: string;
  /** Имената на родителите, в реда parent1, parent2. */
  parentNames: string[];
  /** Имейлите на родителите, в същия ред. */
  parentEmails: string[];
  children: DeclarationMatrixChild[];
}

export interface GetDeclarationMatrixRequest {
  /** Учебна година; по подразбиране активната. Приема и двата правописа. */
  schoolYear?: string;
}

export interface GetDeclarationMatrixResponse {
  /**
   * Колоните — кодовете, които РЕАЛНО са възложени, подредени по реда на
   * `DEFAULT_LEGAL_BASIS`, а непознатите след тях. Изведени от данните, не
   * преписани: списък-константа мълчи, щом училището въведе седма декларация.
   */
  codes: string[];
  /** Код → заглавие на трите езика, за заглавния ред и модала. */
  titles: Partial<Record<string, Record<Language, string>>>;
  /** Всички класове за годината — пълни падащото меню на филтъра. */
  classNames: string[];
  families: DeclarationMatrixFamily[];
  schoolYear: string;
}

export interface GetDeclarationSignatureDetailsRequest {
  assignmentId: string;
}

/**
 * Подробностите зад ЕДНА клетка — това, което модалът показва.
 *
 * `signature` е null при `pending` (няма какво да се покаже) и при възлагане,
 * чийто подпис е бил изтрит извън приложението.
 */
export interface GetDeclarationSignatureDetailsResponse {
  assignmentId: string;
  declarationCode: string;
  declarationTitle: Partial<Record<Language, string>>;
  childId: string;
  /** Каноничното име — от `children`, не от снимката в assignment-а. */
  childName: string;
  /**
   * Регистрираните имейли на семейството (parent1, parent2).
   *
   * Модалът ги предлага за ИЗБОР вместо свободен текст, тоест невалиден вход е
   * невъзможен по конструкция. ⚠️ Сървърът пак валидира — клиентът никога не е
   * авторитет; двете заедно значат, че админът не може да сгреши и случайно.
   */
  parentEmails: string[];
  status: AssignmentStatus;
  statusChangedAtMillis: number | null;
  signature: {
    signatureId: string;
    /** Кой е отговорил — имейлът от самия подпис, не възложеният. */
    parentEmail: string;
    parentName: string;
    /** Кой родителски слот е това в семейството; null, ако имейлът не съвпада. */
    parentSlot: 'parent1' | 'parent2' | null;
    responseType: 'signed' | 'declined';
    responseAtMillis: number | null;
    /** Линк към PDF-а; null при отказ без документ. */
    documentUrl: string | null;
    /** Оттеглен ли е (GDPR чл. 7(3)). */
    isEffective: boolean;
  } | null;
}

export interface ReopenDeclarationRequest {
  /** Задължително без `requestId`. С `requestId` идва от искането, ако липсва. */
  assignmentId?: string;
  /** Защо се връща — ЗАДЪЛЖИТЕЛНА. Празна причина се отказва, не се записва тихо. */
  reason: string;
  /**
   * Искане на родителя през приложението (задача 39Б). Тогава `requestedAt` и
   * `requestedFromEmail` идват ОТ ИСКАНЕТО, не от ръчно въведен текст, и искането се затваря в
   * същата операция. 🔴 Оттеглено съгласие (`revoked`) се връща САМО така.
   */
  requestId?: string;
  /** Кога родителят е поискал връщането (ISO дата, `YYYY-MM-DD`). Задължително без `requestId`. */
  requestedAt?: string;
  /** От кой имейл е дошло искането. Задължително без `requestId`. */
  requestedFromEmail?: string;
}

export interface ReopenDeclarationResponse {
  assignmentId: string;
  /** Състоянието ПРЕДИ връщането — влиза и в одитния запис. */
  previousStatus: AssignmentStatus;
  /**
   * Тръгна ли писмото до родителя.
   *
   * Върнато, а не само логнато: възлагане без `parentEmail` иначе се връща
   * успешно, родителят не научава нищо, а админът е убеден, че е свършил
   * работа. Екранът казва кога това се е случило.
   */
  emailSent: boolean;
}

export interface GetDeclarationDocumentUrlRequest {
  assignmentId: string;
}

export interface GetDeclarationDocumentUrlResponse {
  /**
   * ПРЕСЕН пропуск, издаден в момента на искането, с кратък живот.
   *
   * Не се пази и не се преизползва: всяко натискане иска нов. Точно това прави
   * краткия срок възможен — дългият живот съществуваше заради преизползването.
   */
  url: string;
  /** Кога изтича — екранът го казва на човека, вместо да го оставя да разбере от XML. */
  expiresAtMillis: number;
  /** Подписът, чийто документ е това — за да може екранът да го сравни с показания. */
  signatureId: string;
}
